import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ChalkboardTeacher,
  Users,
  Books,
  Star,
  FileText,
  FilmStrip,
  GameController,
  Plus,
  Trash,
  Spinner,
  Eye,
  ArrowRight,
  ChartBar,
  CheckCircle,
  XCircle,
  ClockCounterClockwise,
  SpeakerHigh,
  ThumbsDown,
  Robot,
  MicrophoneStage,
  UploadSimple,
  UserPlus,
  Envelope,
  Chat,
  ArrowCounterClockwise,
  Warning,
  PencilLine,
  PaperPlaneTilt,
  Bell,
  Sparkle,
  Tag,
  Clock,
  GraduationCap
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import UploadProgress, { makeOnProgress } from '../components/UploadProgress';
import ClassFeedbackInsights from '../components/ClassFeedbackInsights';
import ClassStatsBoard from '../components/ClassStatsBoard';
import ClassRankingBoard from '../components/ClassRankingBoard';
import ExerciseAudioReview from '../components/ExerciseAudioReview';
import MaterialsPage from './MaterialsPage';
import TeamManagement from '../components/TeamManagement';
import BrokenVideosBanner from '../components/BrokenVideosBanner';
import ScheduledPublications from '../components/ScheduledPublications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVELS_ALL = ['A1', 'A2', 'B1', 'B2', 'C1'];

// Relative "last seen" formatter — picks the highest unit that's > 1 (h, d).
// Returns { label, tone } so callers can paint the chip based on freshness.
//   < 24h        → green   ("vor 3h" / "vor 12h" / "gerade eben")
//   < 7 Tage     → amber   ("vor 2 Tagen")
//   ≥ 7 Tage     → red     ("vor 14 Tagen")
//   keine Daten  → stone   ("Noch nie eingeloggt")
const formatLastSeen = (iso) => {
  if (!iso) return { label: 'Noch nie eingeloggt', tone: 'stone', title: '' };
  const seen = new Date(iso);
  if (Number.isNaN(seen.getTime())) return { label: 'Noch nie eingeloggt', tone: 'stone', title: '' };
  const diffMs = Date.now() - seen.getTime();
  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  const title = seen.toLocaleString('de-DE');
  let label;
  if (mins < 5) label = 'gerade eben';
  else if (hours < 1) label = `vor ${mins} Min.`;
  else if (days < 1) label = `vor ${hours} h`;
  else if (days === 1) label = 'gestern';
  else label = `vor ${days} Tagen`;
  const tone = days < 1 ? 'green' : days < 7 ? 'amber' : 'red';
  return { label, tone, title };
};

// Compact attention card column (dudas / foro / voz)
const AttentionGroup = ({ label, count, items, link, renderItem, testId }) => (
  <div className="bg-white/70 rounded-xl border border-amber-200 p-3" data-testid={testId}>
    <div className="flex items-center justify-between mb-2">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-amber-800">{label}</h4>
      <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-full bg-amber-500 text-white text-xs font-bold">
        {count}
      </span>
    </div>
    {count === 0 ? (
      <p className="text-xs text-stone-500 italic">Todo al día ✨</p>
    ) : (
      <div className="space-y-2">
        {items.slice(0, 4).map((it, i) => (
          <Link
            key={i}
            to={link(it)}
            className="block rounded-lg bg-white border border-stone-200 p-2 hover:border-amber-400 transition-colors"
          >
            {renderItem(it)}
          </Link>
        ))}
        {count > 4 && (
          <p className="text-[11px] text-amber-700 text-right font-semibold">
            +{count - 4} más…
          </p>
        )}
      </div>
    )}
  </div>
);

// Inline multi-select level pills. `value` is array, `onChange(nextArray)`.
// Ensures at least one level remains selected.
const LevelPills = ({ value = ['A2'], onChange, testIdPrefix = 'level' }) => {
  const toggle = (lvl) => {
    const has = value.includes(lvl);
    let next = has ? value.filter(l => l !== lvl) : [...value, lvl];
    if (next.length === 0) next = [lvl]; // keep at least one
    // Keep level order A1,A2,...,C1
    next = LEVELS_ALL.filter(l => next.includes(l));
    onChange(next);
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-pills`}>
      {LEVELS_ALL.map(l => {
        const active = value.includes(l);
        return (
          <button
            key={l} type="button" onClick={() => toggle(l)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${active
              ? 'bg-[#AB1C24] text-white border-[#AB1C24] shadow-sm'
              : 'bg-white text-[#78716C] border-stone-300 hover:border-[#AB1C24] hover:text-[#AB1C24]'}`}
            data-testid={`${testIdPrefix}-pill-${l}`}
          >
            {l}
          </button>
        );
      })}
    </div>
  );
};

const TeacherDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Data states
  const [materials, setMaterials] = useState([]);
  const [videos, setVideos] = useState([]);
  const [games, setGames] = useState([]);
  const [exercises, setExercises] = useState([]);

  // Form states
  const [newMaterial, setNewMaterial] = useState({ title: '', description: '', levels: ['A2'], category: 'course', content_url: '', group_id: '' });
  const [newVideo, setNewVideo] = useState({ title: '', description: '', video_url: '', levels: ['A2'], episode: 1, series: 'Miniserie', duration: '', category: 'miniserie', artist: '' });
  const [newGame, setNewGame] = useState({ title: '', description: '', game_type: 'word_match', levels: ['A2'], content: { pairs: [{ es: '', de: '' }, { es: '', de: '' }, { es: '', de: '' }] }, points_reward: 20 });
  const [editingGameId, setEditingGameId] = useState(null);
  const [gameImageUploadingKey, setGameImageUploadingKey] = useState(null);
  const [newExercise, setNewExercise] = useState({ title: '', question: '', exercise_type: 'vocabulary', levels: ['A2'], options: ['', '', '', ''], correct_answer: '' });
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadLabel, setUploadLabel] = useState('');

  // AI generation state
  const [aiGen, setAiGen] = useState({ level: 'A2', exercise_type: 'vocabulary', topic: '', loading: false });

  // Teacher audio upload state
  const [audioEx, setAudioEx] = useState({ title: '', question: '', levels: ['A2'], options: ['', '', '', ''], correct_answer: '', file: null, uploading: false });

  // Users management
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [goalFilter, setGoalFilter] = useState('');
  const [paceFilter, setPaceFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState(''); // '' = all, '__none__' = students without a class, or a group_id

  // Klassen / student groups
  const [groups, setGroups] = useState([]);
  const [newGroup, setNewGroup] = useState({ name: '', level: 'A2', schedule: '' });
  const loadGroups = async () => {
    try {
      const res = await axios.get(`${API}/groups`, { withCredentials: true });
      setGroups(res.data || []);
    } catch (e) { /* silent */ }
  };
  const createGroup = async () => {
    if (!newGroup.name.trim()) { toast.error('Name ist erforderlich'); return; }
    try {
      await axios.post(`${API}/groups`, newGroup, { withCredentials: true });
      toast.success('Klasse erstellt');
      setNewGroup({ name: '', level: 'A2', schedule: '' });
      loadGroups();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };
  const deleteGroup = async (gid, name) => {
    if (!window.confirm(`Klasse "${name}" wirklich löschen? Schüler werden nicht gelöscht.`)) return;
    try {
      await axios.delete(`${API}/groups/${gid}`, { withCredentials: true });
      toast.success('Gelöscht');
      loadGroups();
      loadUsers();
    } catch { toast.error('Fehler'); }
  };
  const assignUserToGroup = async (gid, userId) => {
    try {
      await axios.post(`${API}/groups/${gid}/members`, { user_id: userId }, { withCredentials: true });
      toast.success('Schüler zugewiesen');
      loadGroups();
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };
  const unassignUserFromGroup = async (gid, userId) => {
    try {
      await axios.delete(`${API}/groups/${gid}/members/${userId}`, { withCredentials: true });
      toast.success('Schüler entfernt');
      loadGroups();
      loadUsers();
    } catch { toast.error('Fehler'); }
  };
  const [newFreeUser, setNewFreeUser] = useState({ email: '', name: '', password: '', level: 'A2', months: 12, loading: false });

  // Trash (recycle bin)
  const [trashedUsers, setTrashedUsers] = useState([]);
  const [trashSearch, setTrashSearch] = useState('');

  // Invites
  const [invites, setInvites] = useState([]);
  const [newInvite, setNewInvite] = useState({ email: '', name: '', spanish_level: 'A2', message: '', loading: false });

  // Forum management
  const [allTopics, setAllTopics] = useState([]);
  const [topicPosts, setTopicPosts] = useState({});
  const [expandedTopic, setExpandedTopic] = useState(null);

  // Attention queue (requiere atención)
  const [attention, setAttention] = useState(null);

  // AI costs (teacher-only view)
  const [aiCosts, setAiCosts] = useState(null);
  const [aiCostsLoading, setAiCostsLoading] = useState(false);
  const [aiCostsWeeks, setAiCostsWeeks] = useState(4);

  // Site settings (Verwaltung tab): editable landing video, calendar, 40% coupon
  const [siteSettings, setSiteSettings] = useState({
    video_url: '', calendar_events: [], coupon_40_active: false, coupon_40_label: 'Frühbucher-Rabatt', video_mp4_size: 0, video_mp4_filename: ''
  });
  const [siteVideoUploading, setSiteVideoUploading] = useState(false);
  const [newCalEvent, setNewCalEvent] = useState({ date: '', title: '', note: '' });

  // Custom coupon codes
  const [customCoupons, setCustomCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({ code: '', discount_percent: 20, label: '' });
  const loadCustomCoupons = async () => {
    try {
      const res = await axios.get(`${API}/coupons/custom`, { withCredentials: true });
      setCustomCoupons(res.data || []);
    } catch (e) { /* silent */ }
  };
  const createCustomCoupon = async () => {
    const code = (newCoupon.code || '').trim().toUpperCase();
    if (!code) { toast.error('Code ist erforderlich'); return; }
    try {
      await axios.post(`${API}/coupons/custom`, {
        code,
        discount_percent: parseInt(newCoupon.discount_percent, 10),
        label: newCoupon.label || ''
      }, { withCredentials: true });
      toast.success('Code erstellt');
      setNewCoupon({ code: '', discount_percent: 20, label: '' });
      loadCustomCoupons();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };
  const toggleCustomCoupon = async (code, active) => {
    try {
      await axios.patch(`${API}/coupons/custom/${code}`, { active: !active }, { withCredentials: true });
      loadCustomCoupons();
    } catch { toast.error('Fehler'); }
  };
  const deleteCustomCoupon = async (code) => {
    if (!window.confirm(`Code "${code}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/coupons/custom/${code}`, { withCredentials: true });
      toast.success('Gelöscht');
      loadCustomCoupons();
    } catch { toast.error('Fehler'); }
  };

  const loadSiteSettings = async () => {
    try {
      const res = await axios.get(`${API}/site/settings`);
      setSiteSettings({
        video_url: res.data.video_url || '',
        calendar_events: res.data.calendar_events || [],
        coupon_40_active: !!res.data.coupon_40_active,
        coupon_40_label: res.data.coupon_40_label || 'Frühbucher-Rabatt',
        video_mp4_size: res.data.video_mp4_size || 0,
        video_mp4_filename: res.data.video_mp4_filename || '',
      });
    } catch (e) { /* silent */ }
  };

  const saveSiteSettings = async (patch) => {
    try {
      const res = await axios.patch(`${API}/site/settings`, patch, { withCredentials: true });
      setSiteSettings(s => ({ ...s, ...res.data }));
      toast.success('Gespeichert');
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const uploadSiteVideoMp4 = async (file) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) { toast.error('Datei zu groß (max 12 MB). Für längere Videos bitte YouTube-URL nutzen.'); return; }
    const fd = new FormData(); fd.append('file', file);
    setSiteVideoUploading(true);
    try {
      await axios.post(`${API}/site/settings/video-mp4`, fd, { withCredentials: true });
      toast.success('Video hochgeladen');
      loadSiteSettings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSiteVideoUploading(false); }
  };

  const deleteSiteVideoMp4 = async () => {
    if (!window.confirm('MP4-Video wirklich löschen?')) return;
    try {
      await axios.delete(`${API}/site/settings/video-mp4`, { withCredentials: true });
      toast.success('Gelöscht');
      loadSiteSettings();
    } catch (e) { toast.error('Fehler'); }
  };

  const addCalEvent = async () => {
    if (!newCalEvent.date.trim() || !newCalEvent.title.trim()) {
      toast.error('Datum und Titel erforderlich'); return;
    }
    const evs = [...siteSettings.calendar_events, { ...newCalEvent, id: `ev_${Date.now()}` }];
    await saveSiteSettings({ calendar_events: evs });
    setNewCalEvent({ date: '', title: '', note: '' });
  };

  const removeCalEvent = async (id) => {
    const evs = siteSettings.calendar_events.filter(e => e.id !== id);
    await saveSiteSettings({ calendar_events: evs });
  };

  useEffect(() => { loadAll(); loadUsers(); loadTopics(); loadTrash(); loadInvites(); loadAttention(); loadSiteSettings(); loadCustomCoupons(); loadGroups(); }, []);

  const loadAttention = async () => {
    try {
      const res = await axios.get(`${API}/teacher/attention`, { withCredentials: true });
      setAttention(res.data);
    } catch (e) { /* silent */ }
  };

  const loadAiCosts = async (weeks = aiCostsWeeks) => {
    setAiCostsLoading(true);
    try {
      const res = await axios.get(`${API}/ai/admin/usage?weeks=${weeks}`, { withCredentials: true });
      setAiCosts(res.data);
    } catch (e) {
      toast.error('Fehler beim Laden');
    } finally { setAiCostsLoading(false); }
  };

  const grantAiCredit = async (userId, userName) => {
    const input = window.prompt(
      `Wie viel zusätzliches AI-Guthaben möchtest du ${userName} für diese Woche geben?\n\nIn Euro (z.B. 2.50 für €2,50). Standard: 2,50 €`,
      '2.50'
    );
    if (input === null) return;
    const eur = parseFloat(String(input).replace(',', '.'));
    if (!isFinite(eur) || eur <= 0 || eur > 10) {
      toast.error('Bitte einen Betrag zwischen 0,01 € und 10,00 € angeben');
      return;
    }
    const extra_cents = Math.round(eur * 100);
    try {
      const res = await axios.post(`${API}/ai/admin/grant`, { user_id: userId, extra_cents }, { withCredentials: true });
      toast.success(`+€${(res.data.extra_cents_granted/100).toFixed(2)} für ${userName} · neue Quota: €${(res.data.effective_quota_cents/100).toFixed(2)}`);
      loadAiCosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Gewähren');
    }
  };

  useEffect(() => { if (activeTab === 'ai-costs' && !aiCosts) loadAiCosts(); }, [activeTab]);

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API}/teacher/users`, { withCredentials: true });
      setAllUsers(res.data);
    } catch (e) { console.error(e); }
  };

  const loadTopics = async () => {
    try {
      const res = await axios.get(`${API}/topics?limit=50`, { withCredentials: true });
      setAllTopics(res.data);
    } catch (e) { console.error(e); }
  };

  const loadTrash = async () => {
    try {
      const res = await axios.get(`${API}/teacher/users/trash`, { withCredentials: true });
      setTrashedUsers(res.data);
    } catch (e) { console.error(e); }
  };

  const loadInvites = async () => {
    try {
      const res = await axios.get(`${API}/teacher/invites`, { withCredentials: true });
      setInvites(res.data);
    } catch (e) { console.error(e); }
  };

  const createInvite = async () => {
    if (!newInvite.email) return toast.error('E-Mail erforderlich');
    setNewInvite(p => ({ ...p, loading: true }));
    try {
      await axios.post(`${API}/teacher/invites`, {
        email: newInvite.email,
        name: newInvite.name || null,
        spanish_level: newInvite.spanish_level || 'A2',
        message: newInvite.message || null,
      }, { withCredentials: true });
      toast.success(`Einladung an ${newInvite.email} gesendet`);
      setNewInvite({ email: '', name: '', spanish_level: 'A2', message: '', loading: false });
      loadInvites();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Fehler beim Senden');
      setNewInvite(p => ({ ...p, loading: false }));
    }
  };

  const copyInviteLink = async (inv) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/register?invite=${inv.token}`);
      toast.success('Link kopiert');
    } catch { toast.error('Kopieren fehlgeschlagen'); }
  };

  const revokeInvite = async (inv) => {
    if (!window.confirm(`Einladung für ${inv.email} widerrufen?`)) return;
    try {
      await axios.delete(`${API}/teacher/invites/${inv.invite_id}`, { withCredentials: true });
      toast.success('Einladung widerrufen');
      loadInvites();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Fehler');
    }
  };

  const loadTopicPosts = async (topicId) => {
    try {
      const res = await axios.get(`${API}/forum/posts/${topicId}?limit=100`, { withCredentials: true });
      setTopicPosts(prev => ({ ...prev, [topicId]: res.data }));
    } catch (e) { console.error(e); }
  };

  const createFreeUser = async () => {
    if (!newFreeUser.email.trim() || !/\S+@\S+\.\S+/.test(newFreeUser.email)) {
      return toast.error('Gültige E-Mail erforderlich');
    }
    if (newFreeUser.password && newFreeUser.password.length < 6) {
      return toast.error('Passwort muss mindestens 6 Zeichen haben');
    }
    setNewFreeUser(p => ({ ...p, loading: true }));
    try {
      const res = await axios.post(`${API}/teacher/users/create-free`, {
        email: newFreeUser.email.trim().toLowerCase(),
        name: newFreeUser.name.trim(),
        password: newFreeUser.password || undefined,
        level: newFreeUser.level,
        months: newFreeUser.months
      }, { withCredentials: true });
      if (res.data.already_existed) {
        toast.success('Konto existierte bereits — Premium-Zugang verlängert');
      } else if (res.data.user_chose_password) {
        toast.success(`${res.data.email} erstellt. Passwort: bereits gesetzt.`);
      } else {
        toast.success(`${res.data.email} erstellt. Zugangsdaten per E-Mail gesendet.`);
      }
      setNewFreeUser({ email: '', name: '', password: '', level: 'A2', months: 12, loading: false });
      loadUsers();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Fehler beim Erstellen');
      setNewFreeUser(p => ({ ...p, loading: false }));
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Nutzer ${u.email} in den Papierkorb verschieben?\n\nAlle Daten (Beiträge, Fortschritt, Premium-Status) bleiben erhalten und können innerhalb von 90 Tagen wiederhergestellt werden.`)) return;
    try {
      await axios.delete(`${API}/teacher/users/${u.user_id}`, { withCredentials: true });
      toast.success(`${u.email} in den Papierkorb verschoben`);
      loadUsers();
      loadTrash();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Fehler');
    }
  };

  const setUserPoints = async (u, newPoints) => {
    if (!Number.isFinite(newPoints) || newPoints < 0) return;
    if ((u.points || 0) === newPoints) return;
    try {
      await axios.patch(`${API}/teacher/users/${u.user_id}/points`, { points: newPoints }, { withCredentials: true });
      toast.success(`${u.name || u.email}: ${newPoints} Pkt`);
      loadUsers();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Fehler');
    }
  };

  const adjustUserPoints = async (u, delta) => {
    try {
      await axios.patch(`${API}/teacher/users/${u.user_id}/points`, { delta }, { withCredentials: true });
      toast.success(`${u.name || u.email}: ${(u.points || 0) + delta} Pkt`);
      loadUsers();
    } catch (e) {
      toast.error('Fehler');
    }
  };

  // Inline edit a student's CEFR level / learning goal / learning pace from
  // the user list. Backend validates the value against an allow-list so a
  // bad payload returns 400 — the toast surfaces the detail message so the
  // teacher knows what went wrong.
  const updateUserProfile = async (u, patch) => {
    try {
      await axios.patch(`${API}/teacher/users/${u.user_id}/profile`, patch, { withCredentials: true });
      const label = Object.entries(patch).map(([k, v]) => {
        const niceKey = { spanish_level: 'Niveau', learning_goal: 'Ziel', learning_pace: 'Tempo' }[k] || k;
        const niceVal = {
          travel: 'Reisen', exam: 'Prüfung',
          intensive: 'Intensiv', relaxed: 'Entspannt',
        }[v] || (v || '—');
        return `${niceKey}: ${niceVal}`;
      }).join(' · ');
      toast.success(`${u.name || u.email} → ${label}`);
      loadUsers();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Fehler beim Speichern');
    }
  };

  const restoreUser = async (u) => {
    try {
      await axios.post(`${API}/teacher/users/${u.user_id}/restore`, {}, { withCredentials: true });
      toast.success(`${u.email} wiederhergestellt`);
      loadUsers();
      loadTrash();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Fehler beim Wiederherstellen');
    }
  };

  const purgeUser = async (u) => {
    if (!window.confirm(`ACHTUNG: ${u.email} und ALLE Daten (Beiträge, Fortschritt, Konversationen) werden endgültig gelöscht!\n\nDiese Aktion kann NICHT rückgängig gemacht werden.\n\nBist du dir absolut sicher?`)) return;
    try {
      await axios.delete(`${API}/teacher/users/${u.user_id}/purge`, { withCredentials: true });
      toast.success(`${u.email} endgültig gelöscht`);
      loadTrash();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Fehler');
    }
  };

  const deleteForumPost = async (postId) => {
    if (!window.confirm('Diesen Beitrag und alle Antworten darauf löschen?')) return;
    try {
      await axios.delete(`${API}/teacher/forum/posts/${postId}`, { withCredentials: true });
      toast.success('Beitrag gelöscht');
      // Reload posts for all expanded topics
      Object.keys(topicPosts).forEach(tid => loadTopicPosts(tid));
    } catch (e) { toast.error('Fehler beim Löschen'); }
  };

  const deleteTopic = async (topicId) => {
    if (!window.confirm('Thema und ALLE zugehörigen Beiträge löschen?')) return;
    try {
      await axios.delete(`${API}/teacher/topics/${topicId}`, { withCredentials: true });
      toast.success('Thema gelöscht');
      loadTopics();
    } catch (e) { toast.error('Fehler'); }
  };

  const loadAll = async () => {
    try {
      const [statsRes, matsRes, vidsRes, gamesRes, exsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/materials`, { withCredentials: true }),
        axios.get(`${API}/videos`, { withCredentials: true }),
        axios.get(`${API}/games`, { withCredentials: true }),
        axios.get(`${API}/exercises?limit=100&include_pending=true`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setMaterials(matsRes.data);
      setVideos(vidsRes.data);
      setGames(gamesRes.data);
      setExercises(exsRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Material handlers
  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadPct(0);
    setUploadLabel(file.name);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        `${API}/materials/upload`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: makeOnProgress(setUploadPct),
        }
      );
      setNewMaterial(prev => ({ ...prev, content_url: res.data.storage_path }));
      toast.success('Hochgeladen');
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Unbekannter Fehler';
      console.error('material upload error', e);
      toast.error(`Upload fehlgeschlagen: ${msg}`);
    }
    finally {
      setUploading(false);
      setUploadPct(0);
      setUploadLabel('');
    }
  };

  const saveMaterial = async () => {
    if (!newMaterial.title) return toast.error('Titel erforderlich');
    try {
      await axios.post(`${API}/materials`, newMaterial, { withCredentials: true });
      toast.success('Material erstellt');
      setNewMaterial({ title: '', description: '', levels: ['A2'], category: 'course', content_url: '', group_id: '' });
      loadAll();
    } catch (e) { toast.error('Fehler'); }
  };

  const deleteMaterial = async (id) => {
    await axios.delete(`${API}/materials/${id}`, { withCredentials: true });
    toast.success('Gelöscht');
    loadAll();
  };

  // Video handlers
  const saveVideo = async () => {
    if (!newVideo.title || !newVideo.video_url) return toast.error('Titel und URL erforderlich');
    try {
      await axios.post(`${API}/videos`, newVideo, { withCredentials: true });
      toast.success('Video erstellt');
      setNewVideo({ title: '', description: '', video_url: '', levels: ['A2'], episode: 1, series: 'Miniserie', duration: '', category: 'miniserie', artist: '' });
      loadAll();
    } catch (e) { toast.error('Fehler'); }
  };

  const deleteVideo = async (id) => {
    await axios.delete(`${API}/videos/${id}`, { withCredentials: true });
    toast.success('Gelöscht');
    loadAll();
  };

  // Game handlers
  const DEFAULT_CONTENT = {
    word_match: { pairs: [{ es: '', de: '' }, { es: '', de: '' }, { es: '', de: '' }] },
    sentence_order: { sentences: [{ text: '' }, { text: '' }, { text: '' }, { text: '' }] },
    true_false: { statements: [{ text: '', is_true: true }, { text: '', is_true: false }, { text: '', is_true: true }] },
    odd_one_out: { rounds: [{ category: '', words: ['', '', '', ''], odd_index: 0 }] },
    emoji_quiz: { questions: [{ emoji: '😀', options: ['', '', '', ''], correct: 0 }] },
  };
  const changeGameType = (type) => {
    setNewGame(p => ({ ...p, game_type: type, content: JSON.parse(JSON.stringify(DEFAULT_CONTENT[type] || {})) }));
  };
  // word_match helpers
  const addPair = () => setNewGame(p => ({ ...p, content: { ...p.content, pairs: [...(p.content.pairs || []), { es: '', de: '' }] } }));
  const updatePair = (idx, field, value) => {
    setNewGame(p => {
      const pairs = (p.content.pairs || []).map((pair, i) => i === idx ? { ...pair, [field]: value } : pair);
      return { ...p, content: { ...p.content, pairs } };
    });
  };
  const removePair = (idx) => {
    setNewGame(p => ({ ...p, content: { ...p.content, pairs: (p.content.pairs || []).filter((_, i) => i !== idx) } }));
  };
  // sentence_order helpers
  const addSentence = () => setNewGame(p => ({ ...p, content: { ...p.content, sentences: [...(p.content.sentences || []), { text: '' }] } }));
  const updateSentence = (idx, value) => {
    setNewGame(p => {
      const sentences = (p.content.sentences || []).map((s, i) => i === idx ? { ...s, text: value } : s);
      return { ...p, content: { ...p.content, sentences } };
    });
  };
  const removeSentence = (idx) => {
    setNewGame(p => ({ ...p, content: { ...p.content, sentences: (p.content.sentences || []).filter((_, i) => i !== idx) } }));
  };
  // true_false helpers
  const addStatement = () => setNewGame(p => ({ ...p, content: { ...p.content, statements: [...(p.content.statements || []), { text: '', is_true: true }] } }));
  const updateStatement = (idx, field, value) => {
    setNewGame(p => {
      const statements = (p.content.statements || []).map((s, i) => i === idx ? { ...s, [field]: value } : s);
      return { ...p, content: { ...p.content, statements } };
    });
  };
  const removeStatement = (idx) => {
    setNewGame(p => ({ ...p, content: { ...p.content, statements: (p.content.statements || []).filter((_, i) => i !== idx) } }));
  };
  // odd_one_out helpers
  const addRound = () => setNewGame(p => ({ ...p, content: { ...p.content, rounds: [...(p.content.rounds || []), { category: '', words: ['', '', '', ''], odd_index: 0 }] } }));
  const updateRound = (idx, field, value) => {
    setNewGame(p => {
      const rounds = (p.content.rounds || []).map((r, i) => i === idx ? { ...r, [field]: value } : r);
      return { ...p, content: { ...p.content, rounds } };
    });
  };
  const updateRoundWord = (idx, wi, value) => {
    setNewGame(p => {
      const rounds = (p.content.rounds || []).map((r, i) => {
        if (i !== idx) return r;
        const words = [...(r.words || [])];
        words[wi] = value;
        return { ...r, words };
      });
      return { ...p, content: { ...p.content, rounds } };
    });
  };
  const removeRound = (idx) => {
    setNewGame(p => ({ ...p, content: { ...p.content, rounds: (p.content.rounds || []).filter((_, i) => i !== idx) } }));
  };
  // emoji_quiz helpers
  const addQuestion = () => setNewGame(p => ({ ...p, content: { ...p.content, questions: [...(p.content.questions || []), { emoji: '😀', options: ['', '', '', ''], correct: 0 }] } }));
  const updateQuestion = (idx, field, value) => {
    setNewGame(p => {
      const questions = (p.content.questions || []).map((q, i) => i === idx ? { ...q, [field]: value } : q);
      return { ...p, content: { ...p.content, questions } };
    });
  };
  const updateQuestionOption = (idx, oi, value) => {
    setNewGame(p => {
      const questions = (p.content.questions || []).map((q, i) => {
        if (i !== idx) return q;
        const options = [...(q.options || [])];
        options[oi] = value;
        return { ...q, options };
      });
      return { ...p, content: { ...p.content, questions } };
    });
  };
  const removeQuestion = (idx) => {
    setNewGame(p => ({ ...p, content: { ...p.content, questions: (p.content.questions || []).filter((_, i) => i !== idx) } }));
  };

  // Image upload for game items. `onUrl` callback receives the uploaded image URL.
  const uploadGameImage = async (file, key, onUrl) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Bitte Bilddatei wählen');
    if (file.size > 5 * 1024 * 1024) return toast.error('Bild zu groß (max 5 MB)');
    setGameImageUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/games/upload-image`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onUrl(res.data.image_url);
      toast.success('Bild hochgeladen');
    } catch (e) {
      toast.error('Upload fehlgeschlagen');
    } finally {
      setGameImageUploadingKey(null);
    }
  };

  const startEditGame = (g) => {
    setEditingGameId(g.game_id);
    setNewGame({
      title: g.title || '',
      description: g.description || '',
      game_type: g.game_type || 'word_match',
      levels: g.levels || [g.level || 'A2'],
      content: JSON.parse(JSON.stringify(g.content || DEFAULT_CONTENT[g.game_type] || {})),
      points_reward: g.points_reward || 20,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEditGame = () => {
    setEditingGameId(null);
    setNewGame({ title: '', description: '', game_type: 'word_match', levels: ['A2'], content: JSON.parse(JSON.stringify(DEFAULT_CONTENT.word_match)), points_reward: 20 });
  };

  const saveGame = async () => {
    if (!newGame.title) return toast.error('Titel erforderlich');
    let content = newGame.content;
    if (newGame.game_type === 'word_match') {
      const validPairs = (content.pairs || []).filter(p => p.es && p.de);
      if (validPairs.length < 3) return toast.error('Mindestens 3 Wortpaare');
      content = { pairs: validPairs };
    } else if (newGame.game_type === 'sentence_order') {
      const validSentences = (content.sentences || []).filter(s => s.text);
      if (validSentences.length < 3) return toast.error('Mindestens 3 Sätze');
      content = { sentences: validSentences };
    } else if (newGame.game_type === 'true_false') {
      const validStatements = (content.statements || []).filter(s => s.text);
      if (validStatements.length < 3) return toast.error('Mindestens 3 Aussagen');
      content = { statements: validStatements };
    } else if (newGame.game_type === 'odd_one_out') {
      const validRounds = (content.rounds || []).filter(r => r.words.every(w => w) && r.category);
      if (validRounds.length < 1) return toast.error('Mindestens 1 Runde mit allen Feldern');
      content = { rounds: validRounds };
    } else if (newGame.game_type === 'emoji_quiz') {
      const validQs = (content.questions || []).filter(q => q.emoji && q.options.every(o => o));
      if (validQs.length < 1) return toast.error('Mindestens 1 Frage mit allen Optionen');
      content = { questions: validQs };
    }
    try {
      if (editingGameId) {
        await axios.patch(`${API}/games/${editingGameId}`, { ...newGame, content }, { withCredentials: true });
        toast.success('Spiel aktualisiert');
      } else {
        await axios.post(`${API}/games`, { ...newGame, content }, { withCredentials: true });
        toast.success('Spiel erstellt');
      }
      setEditingGameId(null);
      setNewGame({ title: '', description: '', game_type: 'word_match', levels: ['A2'], content: JSON.parse(JSON.stringify(DEFAULT_CONTENT.word_match)), points_reward: 20 });
      loadAll();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Unbekannter Fehler';
      console.error('saveGame error', e);
      toast.error(`Fehler: ${msg}`);
    }
  };

  const deleteGame = async (id) => {
    await axios.delete(`${API}/games/${id}`, { withCredentials: true });
    toast.success('Gelöscht');
    loadAll();
  };

  // Exercise handlers
  const saveExercise = async () => {
    if (!newExercise.title || !newExercise.question) return toast.error('Titel und Frage erforderlich');
    const validOpts = newExercise.options.filter(o => o.trim());
    if (validOpts.length < 2) return toast.error('Mindestens 2 Optionen');
    if (!newExercise.correct_answer) return toast.error('Richtige Antwort wählen');
    try {
      const exerciseId = `ex_${Date.now().toString(36)}`;
      await axios.post(`${API}/exercises`, {
        ...newExercise,
        level: newExercise.levels[0], // backward compat primary level
        exercise_id: exerciseId, options: validOpts,
        created_by: user.user_id
      }, { withCredentials: true });
      toast.success('Übung erstellt');
      setNewExercise({ title: '', question: '', exercise_type: 'vocabulary', levels: ['A2'], options: ['', '', '', ''], correct_answer: '' });
      loadAll();
    } catch (e) { toast.error('Fehler'); }
  };

  const deleteExercise = async (id) => {
    try {
      await axios.delete(`${API}/exercises/${id}`, { withCredentials: true });
      toast.success('Übung gelöscht');
      loadAll();
      loadReported();
    } catch (e) { toast.error('Fehler'); }
  };

  // AI-generate a written/audio exercise
  const generateAiExercise = async () => {
    setAiGen(p => ({ ...p, loading: true }));
    try {
      const res = await axios.post(`${API}/teacher/exercises/ai-generate`, {
        level: aiGen.level, exercise_type: aiGen.exercise_type, topic: aiGen.topic.trim()
      }, { withCredentials: true });
      toast.success(`Übung erstellt: ${res.data.title}`);
      setAiGen({ level: 'A2', exercise_type: 'vocabulary', topic: '', loading: false });
      loadAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'KI-Generierung fehlgeschlagen');
      setAiGen(p => ({ ...p, loading: false }));
    }
  };

  // Teacher upload audio exercise
  const uploadAudioExercise = async () => {
    if (!audioEx.file) return toast.error('Audio-Datei erforderlich');
    if (!audioEx.question || !audioEx.correct_answer) return toast.error('Frage und richtige Antwort erforderlich');
    const validOpts = audioEx.options.filter(o => o.trim());
    if (validOpts.length < 2) return toast.error('Mindestens 2 Antwortoptionen');
    setAudioEx(p => ({ ...p, uploading: true }));
    try {
      const fd = new FormData();
      fd.append('file', audioEx.file);
      fd.append('title', audioEx.title || `Hörverstehen - ${audioEx.levels[0]}`);
      fd.append('question', audioEx.question);
      fd.append('levels', JSON.stringify(audioEx.levels));
      fd.append('level', audioEx.levels[0]);
      fd.append('correct_answer', audioEx.correct_answer);
      fd.append('options', JSON.stringify(validOpts));
      await axios.post(`${API}/teacher/exercises/audio-upload`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Audio-Übung hochgeladen');
      setAudioEx({ title: '', question: '', levels: ['A2'], options: ['', '', '', ''], correct_answer: '', file: null, uploading: false });
      loadAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Upload fehlgeschlagen');
      setAudioEx(p => ({ ...p, uploading: false }));
    }
  };

  // Reported exercises
  const [reportedExercises, setReportedExercises] = useState([]);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [reviewCount, setReviewCount] = useState(0);
  const loadReported = async () => {
    try {
      const res = await axios.get(`${API}/exercises/reported`, { withCredentials: true });
      setReportedExercises(res.data);
    } catch (e) { console.error(e); }
  };
  const loadReviewQueue = async () => {
    try {
      const res = await axios.get(`${API}/review-queue`, { withCredentials: true });
      setReviewQueue(res.data.queue || []);
      setReviewCount(res.data.total || 0);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { loadReported(); loadReviewQueue(); }, []);

  // Content upload helper (supports optional progress callback)
  const uploadContent = async (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post(
      `${API}/upload/content`,
      formData,
      {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        ...(onProgress ? { onUploadProgress: onProgress } : {}),
      }
    );
    return res.data;
  };

  // Review queue actions
  const reviewAction = async (contentType, contentId, action) => {
    try {
      await axios.post(`${API}/review-queue/action`, { content_type: contentType, content_id: contentId, action }, { withCredentials: true });
      toast.success(action === 'approve' ? 'Genehmigt!' : action === 'reject' ? 'Abgelehnt' : 'Gelöscht');
      loadReviewQueue();
      loadReported();
      loadAll();
    } catch (e) { toast.error('Fehler'); }
  };

  const tabs = [
    { id: 'overview', label: 'Übersicht', icon: ChartBar },
    { id: 'users', label: 'Nutzer', icon: Users },
    { id: 'invites', label: 'Verwaltung', icon: Envelope },
    { id: 'groups', label: 'Klassen', icon: GraduationCap },
    ...(user?.role === 'admin' ? [{ id: 'team', label: 'Team', icon: ChalkboardTeacher }] : []),
    { id: 'trash', label: `Schüler-Papierkorb${trashedUsers.length > 0 ? ` (${trashedUsers.length})` : ''}`, icon: Trash },
    { id: 'materials', label: 'Materialien', icon: FileText },
    { id: 'materials-trash', label: 'Material-Papierkorb', icon: Trash },
    { id: 'videos', label: 'Videos', icon: FilmStrip },
    { id: 'games', label: 'Spiele', icon: GameController },
    { id: 'exercises', label: 'Übungen', icon: Books },
    { id: 'forum', label: 'Forum', icon: Chat },
    { id: 'feedback', label: 'Klassen-Feedback', icon: Star },
    { id: 'reported', label: `Prüfung${reviewCount > 0 ? ` (${reviewCount})` : ''}`, icon: ClockCounterClockwise },
    { id: 'ai-costs', label: 'AI-Kosten', icon: Sparkle },
  ];

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Spinner size={32} className="animate-spin text-[#AB1C24]" /></div>;

  return (
    <div className="space-y-6" data-testid="teacher-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#292524]">Lehrer-Dashboard</h1>
          <p className="text-[#57534E] mt-1">Verwalte Materialien, Videos, Spiele und Übungen</p>
        </div>
        <Link
          to="/teacher/push"
          className="inline-flex items-center gap-2 bg-[#AB1C24] hover:bg-[#8A151C] text-white text-sm font-semibold rounded-lg px-4 py-2.5 self-start sm:self-auto transition-colors"
          data-testid="broadcast-entry-btn"
        >
          <PaperPlaneTilt size={16} weight="fill" />
          Nachricht an Schüler
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#F5F5F4] p-1.5 rounded-xl overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              activeTab === tab.id ? 'bg-white text-[#AB1C24] shadow-sm' : 'text-[#78716C] hover:text-[#292524]'
            }`}
            data-testid={`teacher-tab-${tab.id}`}
          >
            <tab.icon size={18} weight={activeTab === tab.id ? 'fill' : 'regular'} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Team Tab — admin only */}
      {activeTab === 'team' && user?.role === 'admin' && (
        <TeamManagement currentUserId={user?.user_id} />
      )}

      {/* Overview Tab */}
      {activeTab === 'overview' && stats && (
        <div className="space-y-6">
          <ScheduledPublications />
          {/* Attention Queue */}
          {attention && attention.total > 0 && (
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-2xl p-4 sm:p-5" data-testid="teacher-attention-card">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
                    <Bell size={20} weight="fill" />
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-heading font-bold text-base sm:text-lg text-amber-900 leading-tight break-words">Benötigt deine Aufmerksamkeit</h3>
                    <p className="text-xs text-amber-700">{attention.total} ausstehend · Letzte 7 Tage</p>
                  </div>
                </div>
                <button
                  onClick={loadAttention}
                  className="text-xs font-semibold text-amber-700 hover:text-amber-900 underline self-start sm:self-auto flex-shrink-0"
                >
                  Aktualisieren
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <AttentionGroup
                  label="Dudas sin respuesta"
                  count={attention.dudas_pending.count}
                  items={attention.dudas_pending.items}
                  link={(it) => `/dudas/${it.question_id}`}
                  renderItem={(it) => (
                    <>
                      <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                        <span className={`level-badge ${it.level}`}>{it.level}</span>
                        <span className="text-[10px] text-stone-500">· {it.author_name}</span>
                      </div>
                      <p className="text-xs text-stone-700 truncate">{it.title}</p>
                    </>
                  )}
                  testId="attention-dudas"
                />
                <AttentionGroup
                  label="Foro sin respuesta"
                  count={attention.forum_pending.count}
                  items={attention.forum_pending.items}
                  link={() => '/community'}
                  renderItem={(it) => (
                    <>
                      <p className="text-[10px] text-stone-500 mb-0.5">{it.user_name}</p>
                      <p className="text-xs text-stone-700 line-clamp-2">{it.content}</p>
                    </>
                  )}
                  testId="attention-forum"
                />
                <AttentionGroup
                  label="Notas de voz"
                  count={attention.voice_pending.count}
                  items={attention.voice_pending.items}
                  link={() => '/community'}
                  renderItem={(it) => (
                    <>
                      <p className="text-[10px] text-stone-500 mb-0.5">{it.user_name}</p>
                      <p className="text-xs text-stone-700 italic">🎙 {it.content || 'Sprachaufnahme'}</p>
                    </>
                  )}
                  testId="attention-voice"
                />
                {attention.submissions_pending && (
                  <AttentionGroup
                    label="Aufgaben zu korrigieren"
                    count={attention.submissions_pending.count}
                    items={attention.submissions_pending.items}
                    link={() => '/materials?tile=exercises'}
                    renderItem={(it) => (
                      <>
                        <p className="text-[10px] text-stone-500 mb-0.5">{it.user_name} · {it.material_level || ''}</p>
                        <p className="text-xs text-stone-700 italic line-clamp-1">
                          {it.material_title ? `[${it.material_title}] ` : ''}
                          {it.body
                            ? it.body.slice(0, 80) + (it.body.length > 80 ? '…' : '')
                            : (it.audio_url ? '🎙 Sprachnachricht' : (it.files?.length ? `📎 ${it.files.length} Datei(en)` : 'Abgabe'))}
                        </p>
                      </>
                    )}
                    testId="attention-submissions"
                  />
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Nutzer', value: stats.total_users, icon: Users, color: 'text-blue-600' },
              { label: 'Materialien', value: stats.total_materials, icon: FileText, color: 'text-[#AB1C24]' },
              { label: 'Videos', value: stats.total_videos, icon: FilmStrip, color: 'text-[#5B8C9A]' },
              { label: 'Spiele', value: stats.total_games, icon: GameController, color: 'text-purple-600' },
              { label: 'Übungen', value: stats.total_exercises, icon: Books, color: 'text-[#6B8E23]' },
              { label: 'Beiträge', value: stats.total_posts, icon: ChalkboardTeacher, color: 'text-[#57534E]' },
              { label: 'Heute aktiv', value: stats.active_today, icon: Eye, color: 'text-green-600' },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-xl border border-[#D6D3D1] p-4 text-center">
                <s.icon size={24} className={`mx-auto mb-2 ${s.color}`} weight="fill" />
                <span className="block text-2xl font-bold text-[#292524]">{s.value}</span>
                <span className="text-sm text-[#78716C]">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Materials Tab — full Materials UI with per-class / per-subcategory upload */}
      {activeTab === 'materials' && (
        <MaterialsPage />
      )}

      {/* Videos Tab */}
      {activeTab === 'videos' && (
        <div className="space-y-4">
          <BrokenVideosBanner />
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6 space-y-3" data-testid="teacher-video-form">
            <h3 className="font-heading font-semibold text-lg">Neues Video</h3>
            <Input placeholder="Titel" value={newVideo.title} onChange={e => setNewVideo(p => ({...p, title: e.target.value}))} data-testid="t-vid-title" />
            <Input placeholder="Beschreibung" value={newVideo.description} onChange={e => setNewVideo(p => ({...p, description: e.target.value}))} />
            <Input placeholder="Video-URL (YouTube/Vimeo Embed)" value={newVideo.video_url} onChange={e => setNewVideo(p => ({...p, video_url: e.target.value}))} data-testid="t-vid-url" />
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 px-4 py-2 bg-[#F5F5F4] border border-[#D6D3D1] rounded-lg cursor-pointer hover:bg-gray-100 text-sm">
                <FilmStrip size={16} />
                {uploading ? 'Lädt hoch...' : 'Oder Datei hochladen'}
                <input type="file" className="hidden" accept="video/*,audio/*,.mp4,.webm,.mp3" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setUploading(true);
                  setUploadPct(0);
                  setUploadLabel(file.name);
                  try {
                    const r = await uploadContent(file, makeOnProgress(setUploadPct));
                    setNewVideo(p => ({...p, video_url: `${API}/content/${r.storage_path}`}));
                    toast.success('Datei hochgeladen');
                  } catch (err) {
                    const msg = err.response?.data?.detail || err.message || 'Unbekannter Fehler';
                    console.error('video upload error', err);
                    toast.error(`Upload fehlgeschlagen: ${msg}`);
                  }
                  finally {
                    setUploading(false);
                    setUploadPct(0);
                    setUploadLabel('');
                  }
                }} />
              </label>
              {newVideo.video_url && <span className="text-green-600 text-xs">URL gesetzt</span>}
            </div>
            <UploadProgress active={uploading && activeTab === 'videos'} percent={uploadPct} label={uploadLabel} />
            <div>
              <label className="block text-xs font-medium text-[#57534E] mb-1.5">Niveau(s) <span className="text-[#A8A29E]">— mehrere möglich</span></label>
              <LevelPills value={newVideo.levels} onChange={v => setNewVideo(p => ({...p, levels: v}))} testIdPrefix="vid-level" />
            </div>
            <div className="flex gap-3 items-center flex-wrap">
              <select
                value={newVideo.category}
                onChange={e => setNewVideo(p => ({
                  ...p,
                  category: e.target.value,
                  series: e.target.value === 'music' ? 'Música' : 'Miniserie',
                }))}
                className="text-sm border border-stone-300 rounded-md px-2 py-2 bg-white"
                data-testid="t-vid-category"
              >
                <option value="miniserie">Miniserie</option>
                <option value="music">Musik</option>
              </select>
              {newVideo.category === 'music' ? (
                <Input placeholder="Künstler / Artist" className="w-[180px]" value={newVideo.artist}
                  onChange={e => setNewVideo(p => ({ ...p, artist: e.target.value }))} data-testid="t-vid-artist" />
              ) : (
                <Input type="number" placeholder="Folge" className="w-[100px]" value={newVideo.episode}
                  onChange={e => setNewVideo(p => ({ ...p, episode: parseInt(e.target.value) || 1 }))} />
              )}
              <Input placeholder="Serie" className="w-[150px]" value={newVideo.series} onChange={e => setNewVideo(p => ({...p, series: e.target.value}))} />
              <Input placeholder="Dauer (z.B. 12:30)" className="w-[120px]" value={newVideo.duration} onChange={e => setNewVideo(p => ({...p, duration: e.target.value}))} />
            </div>
            <Button onClick={saveVideo} className="bg-[#AB1C24] hover:bg-[#8A151C]" data-testid="t-vid-save"><Plus size={16} className="mr-1" />Erstellen</Button>
          </div>
          <div className="space-y-2">
            {videos.map(v => (
              <div key={v.video_id} className="flex items-center justify-between bg-white rounded-xl border border-[#D6D3D1] px-4 py-3">
                <div>
                  <p className="font-medium text-[#292524]">{v.title}</p>
                  <p className="text-xs text-[#78716C]">Folge {v.episode} — {(v.levels || [v.level]).join(', ')} — {v.series}</p>
                </div>
                <button onClick={() => deleteVideo(v.video_id)} className="p-2 text-red-400 hover:text-red-600"><Trash size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Games Tab */}
      {activeTab === 'games' && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6 space-y-3" data-testid="teacher-game-form">
            <h3 className="font-heading font-semibold text-lg">{editingGameId ? 'Spiel bearbeiten' : 'Neues Spiel'}</h3>
            <Input placeholder="Titel" value={newGame.title} onChange={e => setNewGame(p => ({...p, title: e.target.value}))} data-testid="t-game-title" />
            <Input placeholder="Beschreibung" value={newGame.description} onChange={e => setNewGame(p => ({...p, description: e.target.value}))} />
            <div className="flex gap-3">
              <Select value={newGame.game_type} onValueChange={changeGameType} disabled={!!editingGameId}>
                <SelectTrigger className="w-[220px]" data-testid="t-game-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="word_match">🔗 Wort-Zuordnung</SelectItem>
                  <SelectItem value="sentence_order">📝 Satz-Reihenfolge</SelectItem>
                  <SelectItem value="true_false">✅ Richtig / Falsch</SelectItem>
                  <SelectItem value="odd_one_out">🎯 Eindringling finden</SelectItem>
                  <SelectItem value="emoji_quiz">😀 Emoji-Quiz</SelectItem>
                </SelectContent>
              </Select>
              <Input type="number" placeholder="Punkte" className="w-[120px]" value={newGame.points_reward} onChange={e => setNewGame(p => ({...p, points_reward: parseInt(e.target.value) || 20}))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#57534E] mb-1.5">Niveau(s) <span className="text-[#A8A29E]">— mehrere möglich</span></label>
              <LevelPills value={newGame.levels} onChange={v => setNewGame(p => ({...p, levels: v}))} testIdPrefix="game-level" />
            </div>

            {/* Word Match editor */}
            {newGame.game_type === 'word_match' && (
              <div className="bg-[#F5F5F4] rounded-xl p-4 space-y-2">
                <p className="text-sm font-medium text-[#292524]">Wortpaare (Spanisch — Deutsch) — Foto optional</p>
                {(newGame.content.pairs || []).map((pair, i) => (
                  <div key={i} className="bg-white rounded-lg border border-stone-200 p-2 space-y-2">
                    <div className="flex gap-2 items-center">
                      <Input placeholder="Español" value={pair.es} onChange={e => updatePair(i, 'es', e.target.value)} className="flex-1" />
                      <ArrowRight size={16} className="text-[#78716C]" />
                      <Input placeholder="Deutsch" value={pair.de} onChange={e => updatePair(i, 'de', e.target.value)} className="flex-1" />
                      {(newGame.content.pairs || []).length > 1 && (
                        <button onClick={() => removePair(i)} className="p-1 text-red-400"><Trash size={16} /></button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {pair.image_url ? (
                        <div className="flex items-center gap-2">
                          <img src={pair.image_url} alt="" className="w-10 h-10 rounded object-cover border border-stone-200" />
                          <button onClick={() => updatePair(i, 'image_url', null)} className="text-xs text-red-500 hover:underline">Foto entfernen</button>
                        </div>
                      ) : (
                        <label className="inline-flex items-center gap-1.5 text-xs px-2 py-1 bg-stone-100 hover:bg-stone-200 rounded cursor-pointer">
                          <UploadSimple size={14} />
                          {gameImageUploadingKey === `pair-${i}` ? 'Lädt...' : 'Foto hinzufügen (optional)'}
                          <input type="file" accept="image/*" className="hidden" onChange={e => uploadGameImage(e.target.files?.[0], `pair-${i}`, url => updatePair(i, 'image_url', url))} />
                        </label>
                      )}
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addPair}><Plus size={14} className="mr-1" />Paar hinzufügen</Button>
              </div>
            )}

            {/* Sentence Order editor */}
            {newGame.game_type === 'sentence_order' && (
              <div className="bg-[#F5F5F4] rounded-xl p-4 space-y-2">
                <p className="text-sm font-medium text-[#292524]">Sätze in richtiger Reihenfolge eingeben. Spieler ordnet sie.</p>
                {(newGame.content.sentences || []).map((s, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <span className="text-sm font-bold text-[#78716C] w-6">{i + 1}.</span>
                    <Input placeholder={`Satz ${i + 1}`} value={s.text} onChange={e => updateSentence(i, e.target.value)} className="flex-1" />
                    {(newGame.content.sentences || []).length > 1 && (
                      <button onClick={() => removeSentence(i)} className="p-1 text-red-400"><Trash size={16} /></button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addSentence}><Plus size={14} className="mr-1" />Satz hinzufügen</Button>
              </div>
            )}

            {/* True/False editor */}
            {newGame.game_type === 'true_false' && (
              <div className="bg-[#F5F5F4] rounded-xl p-4 space-y-2">
                <p className="text-sm font-medium text-[#292524]">Aussagen — Spieler entscheidet Richtig oder Falsch</p>
                {(newGame.content.statements || []).map((s, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input placeholder={`Aussage ${i + 1}`} value={s.text} onChange={e => updateStatement(i, 'text', e.target.value)} className="flex-1" />
                    <Select value={s.is_true ? 'true' : 'false'} onValueChange={v => updateStatement(i, 'is_true', v === 'true')}>
                      <SelectTrigger className="w-[110px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">✅ Richtig</SelectItem>
                        <SelectItem value="false">❌ Falsch</SelectItem>
                      </SelectContent>
                    </Select>
                    {(newGame.content.statements || []).length > 1 && (
                      <button onClick={() => removeStatement(i)} className="p-1 text-red-400"><Trash size={16} /></button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addStatement}><Plus size={14} className="mr-1" />Aussage hinzufügen</Button>
              </div>
            )}

            {/* Odd One Out editor */}
            {newGame.game_type === 'odd_one_out' && (
              <div className="bg-[#F5F5F4] rounded-xl p-4 space-y-3">
                <p className="text-sm font-medium text-[#292524]">Runden — 4 Wörter, eines passt nicht (Foto pro Wort optional)</p>
                {(newGame.content.rounds || []).map((r, i) => (
                  <div key={i} className="bg-white rounded-lg p-3 space-y-2 border border-stone-200">
                    <div className="flex gap-2 items-center">
                      <Input placeholder="Kategorie (z.B. Farben, Obst)" value={r.category} onChange={e => updateRound(i, 'category', e.target.value)} className="flex-1" />
                      {(newGame.content.rounds || []).length > 1 && (
                        <button onClick={() => removeRound(i)} className="p-1 text-red-400"><Trash size={16} /></button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {r.words.map((w, wi) => {
                        const imgs = r.images || [];
                        const imgUrl = imgs[wi];
                        return (
                          <div key={wi} className="flex flex-col gap-1 p-2 rounded border border-stone-100 bg-stone-50">
                            <div className="flex gap-1 items-center">
                              <Input placeholder={`Wort ${wi + 1}`} value={w} onChange={e => updateRoundWord(i, wi, e.target.value)} className="flex-1" />
                              <button type="button" onClick={() => updateRound(i, 'odd_index', wi)}
                                className={`px-2 py-1 rounded text-xs font-bold ${r.odd_index === wi ? 'bg-[#AB1C24] text-white' : 'bg-stone-200 text-stone-500'}`}
                                title="Als Eindringling markieren"
                              >✓</button>
                            </div>
                            <div className="flex items-center gap-2">
                              {imgUrl ? (
                                <>
                                  <img src={imgUrl} alt="" className="w-8 h-8 rounded object-cover" />
                                  <button onClick={() => {
                                    const nextImages = [...(r.images || ['', '', '', ''])];
                                    nextImages[wi] = null;
                                    updateRound(i, 'images', nextImages);
                                  }} className="text-xs text-red-500 hover:underline">Entfernen</button>
                                </>
                              ) : (
                                <label className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-white hover:bg-stone-100 border border-stone-200 rounded cursor-pointer">
                                  <UploadSimple size={12} />
                                  {gameImageUploadingKey === `round-${i}-${wi}` ? '...' : 'Foto'}
                                  <input type="file" accept="image/*" className="hidden" onChange={e => uploadGameImage(e.target.files?.[0], `round-${i}-${wi}`, url => {
                                    const nextImages = [...(r.images || ['', '', '', ''])];
                                    nextImages[wi] = url;
                                    updateRound(i, 'images', nextImages);
                                  })} />
                                </label>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-stone-500">Eindringling: <span className="font-semibold text-[#AB1C24]">{r.words[r.odd_index] || '—'}</span></p>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addRound}><Plus size={14} className="mr-1" />Runde hinzufügen</Button>
              </div>
            )}

            {/* Emoji Quiz editor */}
            {newGame.game_type === 'emoji_quiz' && (
              <div className="bg-[#F5F5F4] rounded-xl p-4 space-y-3">
                <p className="text-sm font-medium text-[#292524]">Emoji oder eigene Foto → spanisches Wort</p>
                {(newGame.content.questions || []).map((q, i) => (
                  <div key={i} className="bg-white rounded-lg p-3 space-y-2 border border-stone-200">
                    <div className="flex gap-2 items-start">
                      {q.image_url ? (
                        <div className="flex flex-col items-center gap-1">
                          <img src={q.image_url} alt="" className="w-20 h-20 rounded-lg object-cover border border-stone-200" />
                          <button onClick={() => updateQuestion(i, 'image_url', null)} className="text-xs text-red-500 hover:underline">Foto entfernen</button>
                        </div>
                      ) : (
                        <>
                          <Input placeholder="😀" value={q.emoji} onChange={e => updateQuestion(i, 'emoji', e.target.value)} className="w-[80px] text-2xl text-center" />
                          <div className="flex-1">
                            <span className="text-xs text-stone-500 block mb-1">Emoji einfügen oder:</span>
                            <label className="inline-flex items-center gap-1.5 text-xs px-2 py-1 bg-stone-100 hover:bg-stone-200 rounded cursor-pointer">
                              <UploadSimple size={14} />
                              {gameImageUploadingKey === `q-${i}` ? 'Lädt...' : '📷 Foto hochladen'}
                              <input type="file" accept="image/*" className="hidden" onChange={e => uploadGameImage(e.target.files?.[0], `q-${i}`, url => updateQuestion(i, 'image_url', url))} />
                            </label>
                          </div>
                        </>
                      )}
                      {(newGame.content.questions || []).length > 1 && (
                        <button onClick={() => removeQuestion(i)} className="p-1 text-red-400 ml-auto"><Trash size={16} /></button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {q.options.map((opt, oi) => (
                        <div key={oi} className="flex gap-1 items-center">
                          <Input placeholder={`Option ${oi + 1}`} value={opt} onChange={e => updateQuestionOption(i, oi, e.target.value)} className="flex-1" />
                          <button type="button" onClick={() => updateQuestion(i, 'correct', oi)}
                            className={`px-2 py-1 rounded text-xs font-bold ${q.correct === oi ? 'bg-green-600 text-white' : 'bg-stone-200 text-stone-500'}`}
                            title="Richtige Antwort"
                          >✓</button>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-stone-500">Richtig: <span className="font-semibold text-green-700">{q.options[q.correct] || '—'}</span></p>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addQuestion}><Plus size={14} className="mr-1" />Frage hinzufügen</Button>
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={saveGame} className="bg-[#AB1C24] hover:bg-[#8A151C]" data-testid="t-game-save">
                <Plus size={16} className="mr-1" />{editingGameId ? 'Speichern' : 'Erstellen'}
              </Button>
              {editingGameId && (
                <Button variant="outline" onClick={cancelEditGame} data-testid="t-game-cancel-edit">
                  Abbrechen
                </Button>
              )}
            </div>
          </div>
          <div className="space-y-2">
            {games.map(g => (
              <div key={g.game_id} className={`flex items-center justify-between bg-white rounded-xl border px-4 py-3 ${editingGameId === g.game_id ? 'border-[#AB1C24] ring-1 ring-[#AB1C24]/40' : 'border-[#D6D3D1]'}`} data-testid={`teacher-game-row-${g.game_id}`}>
                <div>
                  <p className="font-medium text-[#292524]">{g.title}</p>
                  <p className="text-xs text-[#78716C]">{(g.levels || [g.level]).join(', ')} — {g.game_type} — {g.points_reward} Pkt</p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => startEditGame(g)} className="p-2 text-stone-500 hover:text-[#AB1C24]" title="Bearbeiten" data-testid={`edit-game-${g.game_id}`}>
                    <PencilLine size={16} />
                  </button>
                  <button onClick={() => deleteGame(g.game_id)} className="p-2 text-red-400 hover:text-red-600" title="Löschen"><Trash size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Exercises Tab */}
      {activeTab === 'exercises' && (
        <div className="space-y-4">
          {/* AI Generator Card */}
          <div className="bg-gradient-to-br from-amber-50 to-white rounded-2xl border border-amber-200 p-6 space-y-3" data-testid="teacher-ai-gen">
            <div className="flex items-center gap-2">
              <Robot size={24} className="text-amber-600" weight="fill" />
              <h3 className="font-heading font-semibold text-lg">KI-Übung generieren</h3>
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Claude Sonnet 4.5</span>
            </div>
            <p className="text-sm text-[#78716C]">Die KI erstellt eine komplette Übung (Frage + 4 Optionen + richtige Antwort + Erklärung). Bei "Hörverstehen" wird zusätzlich Audio mit natürlicher Stimme generiert.</p>
            <div className="flex flex-wrap gap-3">
              <Select value={aiGen.exercise_type} onValueChange={v => setAiGen(p => ({...p, exercise_type: v}))}>
                <SelectTrigger className="w-[180px]" data-testid="ai-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vocabulary">Vokabeln</SelectItem>
                  <SelectItem value="grammar">Grammatik</SelectItem>
                  <SelectItem value="translation">Übersetzung</SelectItem>
                  <SelectItem value="listening">Hörverstehen (mit Audio)</SelectItem>
                </SelectContent>
              </Select>
              <Select value={aiGen.level} onValueChange={v => setAiGen(p => ({...p, level: v}))}>
                <SelectTrigger className="w-[120px]" data-testid="ai-level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="A1">A1</SelectItem><SelectItem value="A2">A2</SelectItem>
                  <SelectItem value="B1">B1</SelectItem><SelectItem value="B2">B2</SelectItem>
                  <SelectItem value="C1">C1</SelectItem>
                </SelectContent>
              </Select>
              <Input placeholder="Thema (optional): z.B. Familie, Reisen..." className="flex-1 min-w-[200px]"
                value={aiGen.topic} onChange={e => setAiGen(p => ({...p, topic: e.target.value}))} data-testid="ai-topic" />
            </div>
            <Button onClick={generateAiExercise} disabled={aiGen.loading}
              className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="ai-gen-btn">
              {aiGen.loading ? <Spinner size={16} className="animate-spin mr-1" /> : <Robot size={16} className="mr-1" weight="fill" />}
              Mit KI erstellen
            </Button>
          </div>

          {/* Audio Upload Card */}
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl border border-blue-200 p-6 space-y-3" data-testid="teacher-audio-upload">
            <div className="flex items-center gap-2">
              <MicrophoneStage size={24} className="text-blue-600" weight="fill" />
              <h3 className="font-heading font-semibold text-lg">Audio-Übung hochladen</h3>
            </div>
            <p className="text-sm text-[#78716C]">Lade deine eigene MP3/WAV-Datei hoch und erstelle eine Hörverstehensübung mit deiner Aufnahme.</p>
            <Input placeholder="Titel (optional)" value={audioEx.title}
              onChange={e => setAudioEx(p => ({...p, title: e.target.value}))} data-testid="aex-title" />
            <Input placeholder="Frage auf Spanisch" value={audioEx.question}
              onChange={e => setAudioEx(p => ({...p, question: e.target.value}))} data-testid="aex-question" />
            <div className="flex gap-3 items-end">
              <div>
                <label className="block text-xs font-medium text-[#57534E] mb-1.5">Niveau(s) <span className="text-[#A8A29E]">— mehrere möglich</span></label>
                <LevelPills value={audioEx.levels} onChange={v => setAudioEx(p => ({...p, levels: v}))} testIdPrefix="aex-level" />
              </div>
              <label className="flex items-center gap-2 px-4 py-2 bg-white border border-[#D6D3D1] rounded-lg cursor-pointer hover:bg-blue-50 text-sm flex-1">
                <UploadSimple size={16} className="text-blue-600" />
                <span className="truncate">{audioEx.file ? audioEx.file.name : 'MP3 oder WAV auswählen...'}</span>
                <input type="file" accept="audio/*,.mp3,.wav,.ogg,.m4a" className="hidden"
                  onChange={e => setAudioEx(p => ({...p, file: e.target.files?.[0] || null}))} data-testid="aex-file" />
              </label>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-[#292524]">Antwortoptionen</p>
              {audioEx.options.map((opt, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input placeholder={`Option ${i+1}`} value={opt} onChange={e => {
                    const opts = [...audioEx.options];
                    opts[i] = e.target.value;
                    setAudioEx(p => ({...p, options: opts}));
                  }} className="flex-1" data-testid={`aex-opt-${i}`} />
                  <button
                    onClick={() => setAudioEx(p => ({...p, correct_answer: opt}))}
                    className={`p-2 rounded-lg ${audioEx.correct_answer === opt && opt ? 'bg-green-100 text-green-600' : 'text-[#78716C] hover:bg-gray-100'}`}
                    title="Als richtig markieren" data-testid={`aex-mark-${i}`}
                  >
                    <Star size={16} weight={audioEx.correct_answer === opt && opt ? 'fill' : 'regular'} />
                  </button>
                </div>
              ))}
            </div>
            <Button onClick={uploadAudioExercise} disabled={audioEx.uploading}
              className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="aex-save">
              {audioEx.uploading ? <Spinner size={16} className="animate-spin mr-1" /> : <UploadSimple size={16} className="mr-1" weight="bold" />}
              Audio-Übung speichern
            </Button>
          </div>

          {/* Manual creation */}
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6 space-y-3" data-testid="teacher-exercise-form">
            <h3 className="font-heading font-semibold text-lg">Neue Übung manuell anlegen</h3>
            <Input placeholder="Titel" value={newExercise.title} onChange={e => setNewExercise(p => ({...p, title: e.target.value}))} data-testid="t-ex-title" />
            <Input placeholder="Frage" value={newExercise.question} onChange={e => setNewExercise(p => ({...p, question: e.target.value}))} />
            <div className="flex gap-3 items-end">
              <Select value={newExercise.exercise_type} onValueChange={v => setNewExercise(p => ({...p, exercise_type: v}))}>
                <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vocabulary">Vokabeln</SelectItem>
                  <SelectItem value="grammar">Grammatik</SelectItem>
                  <SelectItem value="translation">Übersetzung</SelectItem>
                </SelectContent>
              </Select>
              <div>
                <label className="block text-xs font-medium text-[#57534E] mb-1.5">Niveau(s) <span className="text-[#A8A29E]">— mehrere möglich</span></label>
                <LevelPills value={newExercise.levels} onChange={v => setNewExercise(p => ({...p, levels: v}))} testIdPrefix="ex-level" />
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-[#292524]">Antwortoptionen</p>
              {newExercise.options.map((opt, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input placeholder={`Option ${i+1}`} value={opt} onChange={e => {
                    const opts = [...newExercise.options];
                    opts[i] = e.target.value;
                    setNewExercise(p => ({...p, options: opts}));
                  }} className="flex-1" />
                  <button
                    onClick={() => setNewExercise(p => ({...p, correct_answer: opt}))}
                    className={`p-2 rounded-lg ${newExercise.correct_answer === opt && opt ? 'bg-green-100 text-green-600' : 'text-[#78716C] hover:bg-gray-100'}`}
                    title="Als richtig markieren"
                  >
                    <Star size={16} weight={newExercise.correct_answer === opt && opt ? 'fill' : 'regular'} />
                  </button>
                </div>
              ))}
            </div>
            <Button onClick={saveExercise} className="bg-[#AB1C24] hover:bg-[#8A151C]" data-testid="t-ex-save"><Plus size={16} className="mr-1" />Erstellen</Button>
          </div>
          <div className="text-sm text-[#78716C] mb-2">{exercises.length} Übungen insgesamt</div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {exercises.slice(0, 20).map(ex => {
              const hasAudioIssue = ex.audio_issue && (ex.audio_reports_count || 0) > 0;
              const isListening = ex.exercise_type === 'listening';
              return (
                <div key={ex.exercise_id} className={`flex items-center justify-between bg-white rounded-xl border px-4 py-3 ${hasAudioIssue ? 'border-amber-400 ring-1 ring-amber-300/50' : 'border-[#D6D3D1]'}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-[#292524] truncate">{ex.title}</p>
                      {hasAudioIssue && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full inline-flex items-center gap-1" title={`${ex.audio_reports_count} Meldung(en)`}>
                          <Warning size={11} weight="fill" /> Audio-Problem ({ex.audio_reports_count})
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#78716C]">{(ex.levels || [ex.level]).join(', ')} — {ex.exercise_type}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {isListening && (
                      <label className={`p-2 rounded-lg cursor-pointer ${hasAudioIssue ? 'bg-amber-500 text-white hover:bg-amber-600' : 'text-stone-500 hover:bg-blue-50 hover:text-blue-600'}`} title="Eigenes Audio hochladen (ersetzt das aktuelle)" data-testid={`t-ex-audio-${ex.exercise_id}`}>
                        <UploadSimple size={16} />
                        <input type="file" accept="audio/*,.mp3,.wav,.ogg,.m4a" className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (file.size > 15 * 1024 * 1024) return toast.error('Audio zu groß (max 15 MB)');
                            try {
                              const fd = new FormData();
                              fd.append('file', file);
                              const res = await axios.post(`${API}/teacher/exercises/${ex.exercise_id}/audio`, fd, {
                                withCredentials: true,
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              toast.success(`Audio ersetzt · ${res.data?.notified || 0} Schüler benachrichtigt`);
                              loadAll();
                            } catch (err) {
                              toast.error('Upload fehlgeschlagen');
                            } finally {
                              e.target.value = '';
                            }
                          }} />
                      </label>
                    )}
                    <button onClick={() => deleteExercise(ex.exercise_id)} className="p-2 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600" data-testid={`t-ex-del-${ex.exercise_id}`}>
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          {/* Create free user card */}
          <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl border border-green-200 p-6 space-y-4" data-testid="create-free-user">
            <div className="flex items-center gap-2">
              <UserPlus size={24} className="text-green-600" weight="fill" />
              <h3 className="font-heading font-semibold text-lg">Kostenlosen Zugang vergeben</h3>
            </div>
            <p className="text-sm text-[#78716C]">Erstelle ein kostenloses Premium-Konto für einen Schüler. Du kannst ein eigenes Passwort festlegen oder das System eines generieren lassen.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-[#57534E] mb-1">E-Mail-Adresse <span className="text-red-500">*</span></label>
                <Input placeholder="schueler@beispiel.de" value={newFreeUser.email} type="email"
                  onChange={e => setNewFreeUser(p => ({...p, email: e.target.value}))}
                  data-testid="free-user-email" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#57534E] mb-1">Name (optional)</label>
                <Input placeholder="Vorname Nachname" value={newFreeUser.name}
                  onChange={e => setNewFreeUser(p => ({...p, name: e.target.value}))}
                  data-testid="free-user-name" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-[#57534E] mb-1">Passwort <span className="text-[#A8A29E]">— optional, sonst wird ein sicheres Passwort generiert und per E-Mail gesendet</span></label>
              <Input placeholder="Lass leer für automatisches Passwort" value={newFreeUser.password} type="text"
                onChange={e => setNewFreeUser(p => ({...p, password: e.target.value}))}
                data-testid="free-user-password" autoComplete="off" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-[#57534E] mb-1">Spanisch-Niveau</label>
                <Select value={newFreeUser.level} onValueChange={v => setNewFreeUser(p => ({...p, level: v}))}>
                  <SelectTrigger data-testid="free-user-level"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A1">A1 – Anfänger</SelectItem>
                    <SelectItem value="A2">A2 – Grundkenntnisse</SelectItem>
                    <SelectItem value="B1">B1 – Mittelstufe</SelectItem>
                    <SelectItem value="B2">B2 – Fortgeschritten</SelectItem>
                    <SelectItem value="C1">C1 – Sehr fortgeschritten</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#57534E] mb-1">Gültigkeitsdauer (Monate)</label>
                <Input type="number" min="1" max="120"
                  value={newFreeUser.months}
                  onChange={e => setNewFreeUser(p => ({...p, months: parseInt(e.target.value) || 12}))}
                  data-testid="free-user-months" />
                <p className="text-[10px] text-[#A8A29E] mt-0.5">Wie lange der Gratis-Premium-Zugang aktiv bleibt</p>
              </div>
              <div className="flex items-end">
                <Button onClick={createFreeUser} disabled={newFreeUser.loading}
                  className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid="create-free-btn">
                  {newFreeUser.loading ? <Spinner size={16} className="animate-spin mr-1" /> : <Envelope size={16} className="mr-1" weight="fill" />}
                  Konto erstellen
                </Button>
              </div>
            </div>
          </div>

          {/* Users list */}
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-4">
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <h3 className="font-heading font-semibold text-lg">Alle Schüler ({allUsers.length})</h3>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={groupFilter} onChange={e => setGroupFilter(e.target.value)}
                  className="text-sm border border-stone-300 rounded-md px-2 py-1.5 bg-white" data-testid="user-group-filter">
                  <option value="">Alle Klassen</option>
                  <option value="__none__">Ohne Klasse</option>
                  {groups.map(g => <option key={g.group_id} value={g.group_id}>{g.name}{g.level ? ` · ${g.level}` : ''}</option>)}
                </select>
                <select value={goalFilter} onChange={e => setGoalFilter(e.target.value)}
                  className="text-sm border border-stone-300 rounded-md px-2 py-1.5 bg-white" data-testid="user-goal-filter">
                  <option value="">Alle Ziele</option>
                  <option value="travel">Reisen</option>
                  <option value="exam">Prüfung</option>
                </select>
                <select value={paceFilter} onChange={e => setPaceFilter(e.target.value)}
                  className="text-sm border border-stone-300 rounded-md px-2 py-1.5 bg-white" data-testid="user-pace-filter">
                  <option value="">Alle Tempi</option>
                  <option value="intensive">Intensiv</option>
                  <option value="relaxed">Entspannt</option>
                </select>
                <Input placeholder="Suchen..." className="w-[200px]"
                  value={userSearch} onChange={e => setUserSearch(e.target.value)} data-testid="user-search" />
              </div>
            </div>
            <div className="space-y-2 max-h-[560px] overflow-y-auto">
              {allUsers.filter(u => (
                (!userSearch || u.email?.toLowerCase().includes(userSearch.toLowerCase()) || u.name?.toLowerCase().includes(userSearch.toLowerCase()))
                && (!goalFilter || u.learning_goal === goalFilter)
                && (!paceFilter || u.learning_pace === paceFilter)
                && (!groupFilter || (groupFilter === '__none__' ? !u.group_id : u.group_id === groupFilter))
              )).map(u => (
                <div key={u.user_id} className="flex flex-col sm:flex-row sm:items-center justify-between bg-[#FAF9F6] rounded-xl px-4 py-3 hover:bg-stone-100 gap-2" data-testid={`user-row-${u.user_id}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1 w-full sm:w-auto">
                    <div className="w-10 h-10 rounded-full bg-[#AB1C24] flex items-center justify-center text-white font-bold flex-shrink-0">
                      {(u.name || u.email || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-[#292524] break-words">{u.name || '(ohne Name)'}</p>
                      <p className="text-xs text-[#78716C] break-words">{u.email}</p>
                      {/* Inline-editable Lernziel + Lerntempo selectors. Empty = clear. */}
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        <select
                          value={u.learning_goal || ''}
                          onChange={(e) => updateUserProfile(u, { learning_goal: e.target.value })}
                          className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border-0 cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#AB1C24] ${
                            u.learning_goal === 'travel' ? 'bg-sky-100 text-sky-700'
                            : u.learning_goal === 'exam' ? 'bg-violet-100 text-violet-700'
                            : 'bg-stone-100 text-stone-500 italic'
                          }`}
                          data-testid={`user-goal-select-${u.user_id}`}
                          title="Lernziel ändern"
                        >
                          <option value="">— Ziel —</option>
                          <option value="travel">Reisen</option>
                          <option value="exam">Prüfung</option>
                        </select>
                        <select
                          value={u.learning_pace || ''}
                          onChange={(e) => updateUserProfile(u, { learning_pace: e.target.value })}
                          className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded border-0 cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#AB1C24] ${
                            u.learning_pace === 'intensive' ? 'bg-orange-100 text-orange-700'
                            : u.learning_pace === 'relaxed' ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-stone-100 text-stone-500 italic'
                          }`}
                          data-testid={`user-pace-select-${u.user_id}`}
                          title="Lerntempo ändern"
                        >
                          <option value="">— Tempo —</option>
                          <option value="intensive">Intensiv</option>
                          <option value="relaxed">Entspannt</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap flex-shrink-0 pl-13 sm:pl-0">
                    <select
                      value={u.spanish_level || 'A2'}
                      onChange={(e) => updateUserProfile(u, { spanish_level: e.target.value })}
                      className="text-xs px-2 py-0.5 rounded-full bg-white border border-stone-200 cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#AB1C24]"
                      title="CEFR-Niveau ändern"
                      data-testid={`user-level-select-${u.user_id}`}
                    >
                      {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map(lvl => (
                        <option key={lvl} value={lvl}>{lvl}</option>
                      ))}
                    </select>
                    {(() => {
                      const g = groups.find(x => x.group_id === u.group_id);
                      return g
                        ? <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-medium" title={g.name} data-testid={`user-group-${u.user_id}`}>{g.name}</span>
                        : <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-500 italic" data-testid={`user-nogroup-${u.user_id}`}>Ohne Klasse</span>;
                    })()}
                    {(() => {
                      const ls = formatLastSeen(u.last_seen_at);
                      const cls = ls.tone === 'green' ? 'bg-emerald-100 text-emerald-700'
                        : ls.tone === 'amber' ? 'bg-amber-100 text-amber-700'
                        : ls.tone === 'red' ? 'bg-red-100 text-red-700'
                        : 'bg-stone-100 text-stone-500 italic';
                      return (
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 ${cls}`}
                          title={ls.title ? `Zuletzt aktiv: ${ls.title}` : 'Noch nie eingeloggt'}
                          data-testid={`user-last-seen-${u.user_id}`}
                        >
                          <Clock size={11} weight="bold" />
                          {ls.label}
                        </span>
                      );
                    })()}
                    {u.is_premium && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Premium</span>}
                    {u.free_access_granted_by && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Gratis</span>}
                    <div className="flex items-center gap-1 bg-white rounded-md border border-stone-200 px-1.5 py-0.5">
                      <button
                        onClick={() => adjustUserPoints(u, -5)}
                        className="w-5 h-5 rounded text-stone-500 hover:bg-red-100 hover:text-red-600 text-sm font-bold flex items-center justify-center"
                        title="-5 Punkte"
                        data-testid={`points-minus-${u.user_id}`}
                      >−</button>
                      <input
                        type="number"
                        defaultValue={u.points || 0}
                        onBlur={(e) => setUserPoints(u, parseInt(e.target.value, 10))}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        className="w-14 text-xs text-center border-0 focus:outline-none bg-transparent"
                        data-testid={`points-input-${u.user_id}`}
                      />
                      <span className="text-xs text-[#78716C]">Pkt</span>
                      <button
                        onClick={() => adjustUserPoints(u, 5)}
                        className="w-5 h-5 rounded text-stone-500 hover:bg-green-100 hover:text-green-600 text-sm font-bold flex items-center justify-center"
                        title="+5 Punkte"
                        data-testid={`points-plus-${u.user_id}`}
                      >+</button>
                    </div>
                    <Link
                      to={`/teacher/users/${u.user_id}/report`}
                      className="p-2 rounded-lg hover:bg-amber-50 text-amber-500 hover:text-amber-700"
                      title="Informe bi-semanal"
                      data-testid={`report-user-${u.user_id}`}
                    >
                      <FileText size={16} />
                    </Link>
                    <button onClick={() => deleteUser(u)} className="p-2 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600" title="In den Papierkorb verschieben" data-testid={`del-user-${u.user_id}`}>
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              ))}
              {allUsers.length === 0 && <p className="text-center text-[#78716C] py-8">Keine Schüler gefunden.</p>}
            </div>
          </div>
        </div>
      )}

      {/* Invites Tab (renamed Verwaltung) */}
      {activeTab === 'invites' && (
        <div className="space-y-4" data-testid="invites-tab">
          {/* — Site Settings: Video, Calendar, 40% coupon — */}
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-5 space-y-5" data-testid="verwaltung-site-settings">
            <div>
              <h3 className="font-heading font-semibold text-lg text-[#292524]">Landingpage verwalten</h3>
              <p className="text-sm text-[#78716C] mt-0.5">Video, Kalender und Aktions-Rabatt — Änderungen werden sofort öffentlich sichtbar.</p>
            </div>

            {/* Video editor */}
            <div className="bg-[#FAF9F6] rounded-xl p-4 space-y-3">
              <p className="font-semibold text-[#292524] flex items-center gap-2"><FilmStrip size={16} className="text-[#AB1C24]" weight="duotone" /> Methodik-Video „Lerne unsere Lernmethodik kennen“</p>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2">
                <Input value={siteSettings.video_url || ''} onChange={e => setSiteSettings(s => ({ ...s, video_url: e.target.value }))}
                  placeholder="YouTube-URL (z.B. https://youtu.be/...)" data-testid="site-video-url-input" />
                <Button onClick={() => saveSiteSettings({ video_url: siteSettings.video_url })} className="bg-[#AB1C24] hover:bg-[#8A151C]" data-testid="site-video-url-save">YouTube speichern</Button>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <input type="file" accept="video/mp4,video/webm,video/quicktime" id="site-mp4-upload"
                  onChange={e => uploadSiteVideoMp4(e.target.files?.[0])}
                  className="hidden" data-testid="site-mp4-input" />
                <Button onClick={() => document.getElementById('site-mp4-upload')?.click()} variant="outline" disabled={siteVideoUploading} data-testid="site-mp4-upload-btn">
                  {siteVideoUploading ? 'Lädt hoch…' : 'MP4 hochladen (max 12 MB)'}
                </Button>
                {siteSettings.video_mp4_size > 0 && (
                  <>
                    <span className="text-xs text-[#78716C]">{siteSettings.video_mp4_filename} · {(siteSettings.video_mp4_size/1024/1024).toFixed(1)} MB</span>
                    <button onClick={deleteSiteVideoMp4} className="text-xs text-red-600 hover:underline" data-testid="site-mp4-delete">MP4 löschen</button>
                  </>
                )}
              </div>
              <p className="text-xs text-[#A8A29E]">Reihenfolge: YouTube-URL &gt; MP4 &gt; Standard-Demo-Reel.</p>
            </div>

            {/* 40% coupon toggle */}
            <div className="bg-[#FAF9F6] rounded-xl p-4 space-y-3" data-testid="verwaltung-coupon-toggle">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-semibold text-[#292524] flex items-center gap-2"><Tag size={16} className="text-[#AB1C24]" weight="duotone" /> 40% Frühbucher-Rabatt</p>
                  <p className="text-xs text-[#78716C] mt-0.5">Aktiviert das Banner und wendet den Code <code className="bg-white px-1.5 py-0.5 rounded border">MATRIKEL40</code> beim Bezahlen automatisch an.</p>
                </div>
                <button
                  onClick={() => saveSiteSettings({ coupon_40_active: !siteSettings.coupon_40_active })}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm ${siteSettings.coupon_40_active ? 'bg-emerald-600 text-white' : 'bg-stone-200 text-stone-700'}`}
                  data-testid="coupon-40-toggle"
                >
                  {siteSettings.coupon_40_active ? '● Aktiv' : '○ Inaktiv'}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2">
                <Input value={siteSettings.coupon_40_label || ''} onChange={e => setSiteSettings(s => ({ ...s, coupon_40_label: e.target.value }))}
                  placeholder="Banner-Text (z.B. Frühbucher-Rabatt)" data-testid="coupon-40-label-input" />
                <Button onClick={() => saveSiteSettings({ coupon_40_label: siteSettings.coupon_40_label })} variant="outline" data-testid="coupon-40-label-save">Text speichern</Button>
              </div>
            </div>

            {/* Custom discount codes */}
            <div className="bg-[#FAF9F6] rounded-xl p-4 space-y-3" data-testid="verwaltung-custom-coupons">
              <div>
                <p className="font-semibold text-[#292524] flex items-center gap-2"><Tag size={16} className="text-[#AB1C24]" weight="duotone" /> Eigene Rabattcodes</p>
                <p className="text-xs text-[#78716C] mt-0.5">Erstelle individuelle Codes (z.B. für Aktionen, Influencer, Kampagnen). Codes wirken sofort beim Bezahlen.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_1fr_auto] gap-2">
                <Input value={newCoupon.code} onChange={e => setNewCoupon(p => ({ ...p, code: e.target.value.toUpperCase().replace(/\s+/g, '') }))}
                  placeholder="CODE (z.B. SOMMER25)" maxLength={32} data-testid="new-coupon-code" />
                <Input type="number" min="1" max="99" value={newCoupon.discount_percent} onChange={e => setNewCoupon(p => ({ ...p, discount_percent: e.target.value }))}
                  placeholder="%" data-testid="new-coupon-percent" />
                <Input value={newCoupon.label} onChange={e => setNewCoupon(p => ({ ...p, label: e.target.value }))}
                  placeholder="Beschreibung (optional)" maxLength={120} data-testid="new-coupon-label" />
                <Button onClick={createCustomCoupon} className="bg-[#AB1C24] hover:bg-[#8A151C]" data-testid="new-coupon-create">Erstellen</Button>
              </div>
              {customCoupons.length > 0 && (
                <div className="space-y-2 mt-2">
                  {customCoupons.map(c => (
                    <div key={c.code} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-stone-200 gap-3 flex-wrap" data-testid={`coupon-row-${c.code}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-mono font-bold text-[#292524] text-sm flex items-center gap-2">
                          <code className="bg-stone-100 px-2 py-0.5 rounded">{c.code}</code>
                          <span className="text-[#AB1C24]">−{c.discount_percent}%</span>
                          {c.uses_count > 0 && <span className="text-xs font-normal text-[#78716C]">· {c.uses_count}× benutzt</span>}
                        </p>
                        {c.label && <p className="text-xs text-[#78716C] mt-0.5">{c.label}</p>}
                      </div>
                      <button
                        onClick={() => toggleCustomCoupon(c.code, c.active)}
                        className={`px-3 py-1.5 rounded text-xs font-semibold ${c.active ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-stone-200 text-stone-600 hover:bg-stone-300'}`}
                        data-testid={`coupon-toggle-${c.code}`}
                      >{c.active ? '● Aktiv' : '○ Inaktiv'}</button>
                      <button onClick={() => deleteCustomCoupon(c.code)} className="text-xs text-red-600 hover:underline" data-testid={`coupon-del-${c.code}`}>Löschen</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Calendar editor */}
            <div className="bg-[#FAF9F6] rounded-xl p-4 space-y-3" data-testid="verwaltung-calendar">
              <p className="font-semibold text-[#292524] flex items-center gap-2"><Clock size={16} className="text-[#AB1C24]" weight="duotone" /> Anmelde-Kalender</p>
              <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_1fr_auto] gap-2">
                <Input type="text" value={newCalEvent.date} onChange={e => setNewCalEvent(p => ({ ...p, date: e.target.value }))} placeholder="z.B. 15. Mai 2026" data-testid="cal-new-date" />
                <Input value={newCalEvent.title} onChange={e => setNewCalEvent(p => ({ ...p, title: e.target.value }))} placeholder="Titel (z.B. Sommer-Kurs A2)" data-testid="cal-new-title" />
                <Input value={newCalEvent.note} onChange={e => setNewCalEvent(p => ({ ...p, note: e.target.value }))} placeholder="Notiz (optional)" data-testid="cal-new-note" />
                <Button onClick={addCalEvent} className="bg-[#AB1C24] hover:bg-[#8A151C]" data-testid="cal-add-btn">Hinzufügen</Button>
              </div>
              {siteSettings.calendar_events.length > 0 && (
                <div className="space-y-2 mt-2">
                  {siteSettings.calendar_events.map(ev => (
                    <div key={ev.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-stone-200" data-testid={`cal-row-${ev.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-[#292524] text-sm">{ev.date} — {ev.title}</p>
                        {ev.note && <p className="text-xs text-[#78716C] truncate">{ev.note}</p>}
                      </div>
                      <button onClick={() => removeCalEvent(ev.id)} className="text-xs text-red-600 hover:underline ml-3" data-testid={`cal-remove-${ev.id}`}>Löschen</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-white rounded-2xl border border-amber-200 p-5 flex items-start gap-3">
            <Envelope size={22} className="text-amber-600 flex-shrink-0 mt-0.5" weight="fill" />
            <div>
              <h3 className="font-heading font-semibold text-[#292524]">Kostenlose Einladungen</h3>
              <p className="text-sm text-[#78716C] mt-1">
                Lade Personen zu Habla ein. Sie erhalten einen <strong>unbegrenzten kostenlosen Premium-Zugang</strong>.
                Der Einladungslink wird per E-Mail verschickt, kann aber nur <strong>einmal verwendet</strong> werden.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-5">
            <h3 className="font-heading font-semibold text-lg mb-4">Neue Einladung</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>E-Mail *</Label>
                <Input type="email" value={newInvite.email} onChange={e => setNewInvite({ ...newInvite, email: e.target.value })}
                  placeholder="freund@example.com" data-testid="invite-email-input" />
              </div>
              <div>
                <Label>Name (optional)</Label>
                <Input value={newInvite.name} onChange={e => setNewInvite({ ...newInvite, name: e.target.value })}
                  placeholder="Maria" data-testid="invite-name-input" />
              </div>
              <div>
                <Label>Spanisch-Niveau</Label>
                <select value={newInvite.spanish_level} onChange={e => setNewInvite({ ...newInvite, spanish_level: e.target.value })}
                  className="w-full h-10 rounded-md border border-input bg-transparent px-3 text-sm" data-testid="invite-level-select">
                  <option>A1</option><option>A2</option><option>B1</option><option>B2</option><option>C1</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <Label>Persönliche Nachricht (optional)</Label>
                <textarea value={newInvite.message} onChange={e => setNewInvite({ ...newInvite, message: e.target.value })}
                  placeholder="Freu mich, dich bei Habla zu sehen!"
                  className="w-full min-h-[80px] rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                  data-testid="invite-message-input" />
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <Button onClick={createInvite} disabled={newInvite.loading || !newInvite.email}
                className="bg-[#AB1C24] hover:bg-[#8A151C]" data-testid="invite-send-btn">
                {newInvite.loading ? 'Wird gesendet...' : 'Einladung senden'}
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-4">
            <h3 className="font-heading font-semibold text-lg mb-4">Alle Einladungen ({invites.length})</h3>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {invites.map(inv => (
                <div key={inv.invite_id} className="flex items-center justify-between bg-[#FAF9F6] rounded-xl px-4 py-3 gap-2" data-testid={`invite-row-${inv.invite_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-[#292524] truncate">{inv.name || inv.email}</p>
                      {inv.status === 'consumed' ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">✓ Verwendet</span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Ausstehend</span>
                      )}
                    </div>
                    <p className="text-xs text-[#78716C] truncate">{inv.email}</p>
                    <p className="text-[10px] text-[#A8A29E] mt-0.5">
                      {new Date(inv.created_at).toLocaleDateString('de-DE')} · {inv.spanish_level}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {inv.status !== 'consumed' && (
                      <>
                        <button onClick={() => copyInviteLink(inv)}
                          className="px-2 py-1 text-xs rounded-md border border-stone-200 hover:border-[#AB1C24] hover:text-[#AB1C24]"
                          data-testid={`invite-copy-${inv.invite_id}`}>
                          Link kopieren
                        </button>
                        <button onClick={() => revokeInvite(inv)}
                          className="p-2 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600"
                          title="Zurückziehen" data-testid={`invite-revoke-${inv.invite_id}`}>
                          <Trash size={14} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {invites.length === 0 && <p className="text-center text-[#78716C] py-8">Noch keine Einladungen.</p>}
            </div>
          </div>
        </div>
      )}

      {/* Klassen / Student Groups Tab */}
      {activeTab === 'groups' && (
        <div className="space-y-4" data-testid="groups-tab">
          <div className="bg-gradient-to-br from-violet-50 to-white rounded-2xl border border-violet-200 p-5 flex items-start gap-3">
            <GraduationCap size={22} className="text-violet-600 flex-shrink-0 mt-0.5" weight="fill" />
            <div>
              <h3 className="font-heading font-semibold text-[#292524]">Klassen verwalten</h3>
              <p className="text-sm text-[#78716C] mt-1">
                Erstelle Klassen (z.B. „Klasse 03 — A2 — Dienstag 20:00") und weise Schüler zu. Material, das einer Klasse zugeordnet ist, sehen <strong>nur deren Mitglieder</strong>.
              </p>
            </div>
          </div>

          {/* Create new group */}
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-5 space-y-3" data-testid="create-group-block">
            <h3 className="font-heading font-semibold text-lg text-[#292524]">Neue Klasse erstellen</h3>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_1fr_auto] gap-2">
              <Input value={newGroup.name} onChange={e => setNewGroup(p => ({ ...p, name: e.target.value }))}
                placeholder="Name (z.B. Klasse 03)" maxLength={120} data-testid="new-group-name" />
              <select value={newGroup.level} onChange={e => setNewGroup(p => ({ ...p, level: e.target.value }))}
                className="border border-stone-300 rounded-md px-2 py-2 bg-white text-sm" data-testid="new-group-level">
                <option value="A1">A1</option>
                <option value="A2">A2</option>
                <option value="B1">B1</option>
                <option value="B2">B2</option>
                <option value="C1">C1</option>
              </select>
              <Input value={newGroup.schedule} onChange={e => setNewGroup(p => ({ ...p, schedule: e.target.value }))}
                placeholder="Plan (z.B. Dienstag 20:00)" maxLength={200} data-testid="new-group-schedule" />
              <Button onClick={createGroup} className="bg-[#AB1C24] hover:bg-[#8A151C]" data-testid="new-group-create">Erstellen</Button>
            </div>
          </div>

          {groups.length === 0 ? (
            <div className="bg-white rounded-2xl border border-[#D6D3D1] p-8 text-center text-[#78716C]" data-testid="groups-empty">
              Noch keine Klassen erstellt.
            </div>
          ) : (
            <div className="space-y-3">
              {groups.map(g => (
                <div key={g.group_id} className="bg-white rounded-2xl border border-[#D6D3D1] p-5 space-y-3" data-testid={`group-card-${g.group_id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                        <GraduationCap size={20} weight="duotone" className="text-violet-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-heading font-semibold text-[#292524]">{g.name}</p>
                        <p className="text-xs text-[#78716C]">
                          <span className={`level-badge ${g.level} mr-2`}>{g.level}</span>
                          {g.schedule || 'Kein Plan'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-stone-100 text-stone-700 px-2.5 py-1 rounded-full font-medium" data-testid={`group-count-${g.group_id}`}>
                        {g.member_count || 0} Schüler
                      </span>
                      <button onClick={() => deleteGroup(g.group_id, g.name)}
                        className="text-xs text-red-600 hover:underline" data-testid={`group-delete-${g.group_id}`}>Löschen</button>
                    </div>
                  </div>

                  {/* Members + assignment */}
                  <div className="bg-[#FAF9F6] rounded-lg p-3 space-y-2">
                    {(g.members || []).length > 0 ? (
                      <div className="space-y-1.5">
                        {g.members.map(m => (
                          <div key={m.user_id} className="flex items-center justify-between bg-white rounded px-3 py-1.5 text-sm" data-testid={`group-member-${g.group_id}-${m.user_id}`}>
                            <span className="truncate">{m.name || m.email} <span className="text-[#A8A29E] text-xs">· {m.spanish_level}</span></span>
                            <button onClick={() => unassignUserFromGroup(g.group_id, m.user_id)}
                              className="text-xs text-red-600 hover:underline" data-testid={`group-unassign-${m.user_id}`}>Entfernen</button>
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-xs text-[#78716C]">Noch keine Schüler zugewiesen.</p>}
                    {/* Add user dropdown */}
                    <div className="flex items-center gap-2 pt-1 border-t border-stone-200">
                      <select
                        className="text-sm border border-stone-300 rounded-md px-2 py-1.5 bg-white flex-1 min-w-0"
                        defaultValue=""
                        onChange={e => { if (e.target.value) { assignUserToGroup(g.group_id, e.target.value); e.target.value = ''; } }}
                        data-testid={`group-assign-select-${g.group_id}`}
                      >
                        <option value="" disabled>Schüler hinzufügen…</option>
                        {allUsers
                          .filter(u => !u.is_deleted && u.role !== 'admin' && u.group_id !== g.group_id)
                          .map(u => (
                            <option key={u.user_id} value={u.user_id}>
                              {u.name || u.email} · {u.spanish_level || '?'}{u.group_id ? ' (zugewiesen anderer Klasse)' : ''}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Compact ranking of every class by auto-graded accuracy — shows
              which class needs the most attention at a glance. */}
          <ClassRankingBoard />

          {/* Per-class learning stats — at the END of the Klassen tab per user
              request. Aggregates correct/incorrect for auto-graded exercises
              and the teacher's 1–10 score for Sprechen/Schreiben submissions. */}
          <ClassStatsBoard groups={groups} />
        </div>
      )}

      {/* Trash (Recycle Bin) Tab */}
      {activeTab === 'trash' && (
        <div className="space-y-4" data-testid="trash-tab">
          <div className="bg-gradient-to-br from-amber-50 to-white rounded-2xl border border-amber-200 p-5 flex items-start gap-3">
            <Warning size={22} className="text-amber-600 flex-shrink-0 mt-0.5" weight="fill" />
            <div>
              <h3 className="font-heading font-semibold text-[#292524]">Papierkorb</h3>
              <p className="text-sm text-[#78716C] mt-1">
                Gelöschte Nutzer bleiben hier <strong>90 Tage</strong>. Alle Daten (Beiträge, Fortschritt, Premium-Status) werden beibehalten
                und können wiederhergestellt werden. Danach erfolgt die endgültige Löschung automatisch.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-lg">Gelöschte Nutzer ({trashedUsers.length})</h3>
              <Input placeholder="Suchen..." className="w-[220px]"
                value={trashSearch} onChange={e => setTrashSearch(e.target.value)} data-testid="trash-search" />
            </div>
            <div className="space-y-2 max-h-[560px] overflow-y-auto">
              {trashedUsers.filter(u => !trashSearch ||
                u.email?.toLowerCase().includes(trashSearch.toLowerCase()) ||
                u.name?.toLowerCase().includes(trashSearch.toLowerCase())
              ).map(u => {
                const deletedDate = u.deleted_at ? new Date(u.deleted_at).toLocaleDateString('de-DE') : '—';
                const days = u.days_remaining;
                const urgent = typeof days === 'number' && days <= 7;
                return (
                  <div key={u.user_id} className="flex items-center justify-between bg-[#FAF9F6] rounded-xl px-4 py-3 hover:bg-stone-100" data-testid={`trash-row-${u.user_id}`}>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-full bg-stone-400 flex items-center justify-center text-white font-bold flex-shrink-0 opacity-70">
                        {(u.name || u.email || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-[#292524] truncate line-through decoration-stone-400">{u.name || '(ohne Name)'}</p>
                        <p className="text-xs text-[#78716C] truncate">{u.email}</p>
                        <p className="text-[10px] text-[#A8A29E] mt-0.5">
                          Gelöscht am {deletedDate}
                          {typeof days === 'number' && (
                            <span className={urgent ? 'text-red-600 font-semibold ml-2' : 'ml-2'}>
                              · noch {days} Tag{days === 1 ? '' : 'e'}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-white border border-stone-200">{u.spanish_level || 'A2'}</span>
                      {u.is_premium && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Premium</span>}
                      <span className="text-xs text-[#78716C]">{u.points || 0} Pkt</span>
                      <Button onClick={() => restoreUser(u)} size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white h-8 px-3"
                        data-testid={`restore-user-${u.user_id}`}>
                        <ArrowCounterClockwise size={14} className="mr-1" weight="bold" />
                        Wiederherstellen
                      </Button>
                      <button onClick={() => purgeUser(u)}
                        className="p-2 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600"
                        title="Endgültig löschen"
                        data-testid={`purge-user-${u.user_id}`}>
                        <Trash size={16} weight="fill" />
                      </button>
                    </div>
                  </div>
                );
              })}
              {trashedUsers.length === 0 && (
                <div className="text-center py-10">
                  <Trash size={36} className="mx-auto text-[#D6D3D1] mb-3" />
                  <p className="text-[#78716C]">Der Papierkorb ist leer.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Material Trash Tab — soft-deleted teaching material (Materialien tab) */}
      {activeTab === 'materials-trash' && (
        <MaterialsTrashSection />
      )}

      {/* Forum Tab */}
      {activeTab === 'forum' && (
        <div className="space-y-4">
          <div>
            <h3 className="font-heading font-semibold text-lg text-[#292524]">Conversación — Tagesthemen & Beiträge</h3>
            <p className="text-sm text-[#78716C]">Lösche unangemessene Beiträge oder ganze Themen.</p>
          </div>
          <div className="space-y-3">
            {allTopics.map(t => (
              <div key={t.topic_id} className="bg-white rounded-xl border border-[#D6D3D1] overflow-hidden" data-testid={`topic-${t.topic_id}`}>
                <div className="flex items-center justify-between p-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs bg-[#AB1C24]/10 text-[#AB1C24] px-2 py-0.5 rounded-full font-medium">{t.level}</span>
                      <span className="text-xs text-[#78716C]">{t.response_count || 0} Antworten</span>
                    </div>
                    <p className="font-medium text-[#292524]">{t.title}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => {
                      const isOpen = expandedTopic === t.topic_id;
                      setExpandedTopic(isOpen ? null : t.topic_id);
                      if (!isOpen && !topicPosts[t.topic_id]) loadTopicPosts(t.topic_id);
                    }} data-testid={`toggle-topic-${t.topic_id}`}>
                      {expandedTopic === t.topic_id ? 'Einklappen' : 'Beiträge anzeigen'}
                    </Button>
                    <button onClick={() => deleteTopic(t.topic_id)} className="p-2 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600" title="Thema löschen" data-testid={`del-topic-${t.topic_id}`}>
                      <Trash size={18} />
                    </button>
                  </div>
                </div>
                {expandedTopic === t.topic_id && (
                  <div className="border-t border-stone-100 bg-[#FAF9F6] px-4 py-3 space-y-2">
                    {(topicPosts[t.topic_id] || []).length === 0 ? (
                      <p className="text-sm text-[#78716C] py-2">Noch keine Beiträge.</p>
                    ) : (
                      (topicPosts[t.topic_id] || []).map(p => (
                        <div key={p.post_id} className="flex items-start justify-between gap-3 bg-white rounded-lg p-3" data-testid={`post-${p.post_id}`}>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-sm font-medium text-[#292524]">{p.user_name}</p>
                              <span className="text-xs text-[#78716C]">{new Date(p.created_at).toLocaleDateString('de-DE')}</span>
                            </div>
                            <p className="text-sm text-[#57534E] whitespace-pre-wrap">{p.content}</p>
                          </div>
                          <button onClick={() => deleteForumPost(p.post_id)} className="p-2 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 flex-shrink-0" title="Beitrag löschen" data-testid={`del-post-${p.post_id}`}>
                            <Trash size={16} />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))}
            {allTopics.length === 0 && <p className="text-center text-[#78716C] py-8">Keine Themen gefunden.</p>}
          </div>
        </div>
      )}

      {/* Anonymous Class Feedback Tab */}
      {activeTab === 'feedback' && <ClassFeedbackInsights />}

      {/* Review Queue Tab */}
      {activeTab === 'reported' && (
        <div className="space-y-4">
          <div>
            <h3 className="font-heading font-semibold text-lg text-[#292524]">Prüfungswarteschlange</h3>
            <p className="text-sm text-[#78716C]">KI-generierte Inhalte und von Schülern gemeldete Übungen. Genehmige, lehne ab oder lösche.</p>
          </div>

          {reviewQueue.length > 0 ? (
            <div className="space-y-3">
              {reviewQueue.map((item, idx) => {
                const id = item.exercise_id || item.video_id || item.material_id || item.game_id;
                const isPending = item.status === 'pending_review';
                const isReported = item.is_reported;
                return (
                  <div key={`${item.content_type}-${id}-${idx}`} className={`bg-white rounded-xl border p-5 ${isReported ? 'border-red-200' : 'border-amber-200'}`} data-testid={`review-${id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Badges */}
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                            item.content_type === 'exercise' ? 'bg-blue-100 text-blue-700' :
                            item.content_type === 'video' ? 'bg-purple-100 text-purple-700' :
                            item.content_type === 'material' ? 'bg-green-100 text-green-700' :
                            'bg-orange-100 text-orange-700'
                          }`}>
                            {item.content_type === 'exercise' ? 'Übung' : item.content_type === 'video' ? 'Video' : item.content_type === 'material' ? 'Material' : 'Spiel'}
                          </span>
                          {item.level && <span className={`level-badge ${item.level}`}>{item.level}</span>}
                          {item.source === 'ai_generated' && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">KI-generiert</span>}
                          {isPending && <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">Wartet auf Prüfung</span>}
                          {isReported && <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full flex items-center gap-1"><ThumbsDown size={10} />{item.report_count}x gemeldet</span>}
                        </div>
                        {/* Title & Content */}
                        <p className="font-medium text-[#292524]">{item.title || item.question || 'Ohne Titel'}</p>
                        {item.question && item.title && <p className="text-sm text-[#57534E] mt-0.5">{item.question}</p>}
                        {item.exercise_type && <p className="text-xs text-[#78716C] mt-1">Typ: {item.exercise_type}</p>}
                        {item.correct_answer && <p className="text-xs text-green-600 mt-0.5">Richtig: {item.correct_answer}</p>}
                        {item.audio_text && (
                          <p className="text-xs text-[#78716C] mt-1 italic flex items-center gap-1">
                            <SpeakerHigh size={12} /> "{item.audio_text.substring(0, 80)}..."
                          </p>
                        )}
                        {item.description && <p className="text-xs text-[#78716C] mt-0.5">{item.description}</p>}

                        {/* Audio preview + replacement for listening exercises. */}
                        {item.content_type === 'exercise' && item.exercise_type === 'listening' && (
                          <ExerciseAudioReview
                            exerciseId={id}
                            onReplaced={loadReviewQueue}
                          />
                        )}
                      </div>
                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => reviewAction(item.content_type, id, 'approve')}
                          className="p-2.5 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700 transition-colors"
                          title="Genehmigen"
                          data-testid={`approve-${id}`}
                        >
                          <CheckCircle size={20} weight="fill" />
                        </button>
                        <button
                          onClick={() => reviewAction(item.content_type, id, 'reject')}
                          className="p-2.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 hover:text-amber-700 transition-colors"
                          title="Ablehnen"
                          data-testid={`reject-${id}`}
                        >
                          <XCircle size={20} weight="fill" />
                        </button>
                        <button
                          onClick={() => reviewAction(item.content_type, id, 'delete')}
                          className="p-2.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-700 transition-colors"
                          title="Löschen"
                          data-testid={`delete-${id}`}
                        >
                          <Trash size={20} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-xl border border-[#D6D3D1]">
              <CheckCircle size={48} className="mx-auto text-green-400 mb-3" weight="fill" />
              <p className="font-medium text-[#292524] mb-1">Alles geprüft!</p>
              <p className="text-sm text-[#78716C]">Keine Inhalte in der Warteschlange.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'ai-costs' && (
        <div className="space-y-5" data-testid="ai-costs-tab">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="font-heading font-bold text-2xl text-[#292524] flex items-center gap-2">
                <Sparkle size={22} weight="fill" className="text-[#AB1C24]" />
                AI-Nutzung &amp; Kosten
              </h2>
              <p className="text-sm text-[#78716C]">Schätzwert pro Alumno · nur sichtbar für dich</p>
              <p className="text-xs text-emerald-700 mt-1">Tipp: Mit dem Button <span className="font-semibold">+€ Geben</span> neben jedem Alumno kannst du zusätzliches AI-Guthaben für diese Woche vergeben (auch wenn der Alumno noch nichts verbraucht hat).</p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={aiCostsWeeks}
                onChange={(e) => { const n = Number(e.target.value); setAiCostsWeeks(n); loadAiCosts(n); }}
                className="px-3 py-1.5 rounded-lg border border-stone-300 text-sm bg-white"
                data-testid="ai-costs-weeks-select"
              >
                {[1, 4, 8, 12, 26].map(w => <option key={w} value={w}>Letzte {w} {w === 1 ? 'Woche' : 'Wochen'}</option>)}
              </select>
              <button onClick={() => loadAiCosts()} className="px-3 py-1.5 rounded-lg bg-[#AB1C24] text-white text-sm font-semibold hover:bg-[#8A151C]" data-testid="ai-costs-refresh-btn">
                {aiCostsLoading ? 'Lade…' : 'Aktualisieren'}
              </button>
            </div>
          </div>

          {aiCostsLoading && !aiCosts ? (
            <p className="text-center text-[#78716C] py-8">Lade AI-Nutzung…</p>
          ) : aiCosts ? (
            <>
              {/* Totals */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-gradient-to-br from-[#AB1C24]/10 to-[#AB1C24]/5 border border-[#AB1C24]/20 rounded-xl p-4">
                  <p className="text-xs text-[#57534E] uppercase tracking-wider font-semibold">Gesamt</p>
                  <p className="text-3xl font-bold text-[#AB1C24]" data-testid="ai-costs-total-eur">€ {aiCosts.totals.eur.toFixed(2)}</p>
                  <p className="text-xs text-[#78716C] mt-1">{aiCosts.totals.calls} calls</p>
                </div>
                <div className="bg-white border border-stone-200 rounded-xl p-4">
                  <p className="text-xs text-[#57534E] uppercase tracking-wider font-semibold">Alumnos activos</p>
                  <p className="text-3xl font-bold text-[#292524]">{aiCosts.totals.students}</p>
                  <p className="text-xs text-[#78716C] mt-1">en este rango</p>
                </div>
                <div className="bg-white border border-stone-200 rounded-xl p-4">
                  <p className="text-xs text-[#57534E] uppercase tracking-wider font-semibold">Quota semanal</p>
                  <p className="text-3xl font-bold text-emerald-700">€ {aiCosts.quota_eur_per_week.toFixed(2)}</p>
                  <p className="text-xs text-[#78716C] mt-1">por alumno</p>
                </div>
                <div className={`rounded-xl p-4 border ${aiCosts.totals.over_quota_this_week > 0 ? 'bg-amber-50 border-amber-300' : 'bg-white border-stone-200'}`}>
                  <p className="text-xs text-[#57534E] uppercase tracking-wider font-semibold">Sobre cuota</p>
                  <p className={`text-3xl font-bold ${aiCosts.totals.over_quota_this_week > 0 ? 'text-amber-700' : 'text-stone-400'}`}>
                    {aiCosts.totals.over_quota_this_week}
                  </p>
                  <p className="text-xs text-[#78716C] mt-1">esta semana</p>
                </div>
              </div>

              {/* Per-user table */}
              <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-stone-200 flex items-center justify-between">
                  <h3 className="font-semibold text-[#292524]">Detalle por alumno</h3>
                  <p className="text-xs text-[#78716C]">{aiCosts.current_week} · ordenado por uso actual</p>
                </div>
                {aiCosts.users.length === 0 ? (
                  <p className="text-center text-sm text-[#78716C] py-8 italic">Noch keine AI-Nutzung in diesem Zeitraum.</p>
                ) : (
                  <div className="divide-y divide-stone-100">
                    {aiCosts.users.map((u) => {
                      const pct = Math.min(100, u.current_week_pct);
                      const barColor = u.over_quota_this_week ? 'bg-red-500' : pct > 75 ? 'bg-amber-500' : pct > 40 ? 'bg-yellow-400' : 'bg-emerald-500';
                      const isStaff = u.role === 'admin' || u.role === 'teacher';
                      return (
                        <div key={u.user_id} className="px-4 py-3 flex items-center gap-3" data-testid={`ai-cost-user-${u.user_id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-[#292524]">{u.name}</span>
                              {u.spanish_level && <span className={`level-badge ${u.spanish_level}`}>{u.spanish_level}</span>}
                              {isStaff && <span className="text-[9px] uppercase font-bold tracking-wider bg-[#AB1C24] text-white px-1.5 py-0.5 rounded">Staff · exento</span>}
                              {u.current_week_bonus_cents > 0 && (
                                <span className="text-[9px] uppercase font-bold tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded" data-testid={`bonus-badge-${u.user_id}`}>
                                  +€{u.current_week_bonus_eur.toFixed(2)} bonus
                                </span>
                              )}
                              {u.over_quota_this_week && !isStaff && <span className="text-[9px] uppercase font-bold tracking-wider bg-red-100 text-red-700 border border-red-200 px-1.5 py-0.5 rounded">Sobre cuota</span>}
                            </div>
                            <p className="text-xs text-[#78716C] truncate">{u.email} · {u.calls_total} calls · {aiCostsWeeks} semana{aiCostsWeeks === 1 ? '' : 's'}</p>
                            {!isStaff && (
                              <div className="mt-1.5 flex items-center gap-2">
                                <div className="flex-1 h-1.5 rounded-full bg-stone-100 overflow-hidden">
                                  <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-[11px] text-[#57534E] font-medium whitespace-nowrap">
                                  {pct}% · €{(u.current_week_quota_cents/100).toFixed(2)}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0 flex items-center gap-3">
                            <div>
                              <p className="font-bold text-[#292524]">€ {u.used_eur_total.toFixed(2)}</p>
                              <p className="text-[10px] text-[#78716C] uppercase tracking-wider">Total</p>
                              <p className="text-[11px] text-[#57534E] mt-1">esta semana: € {u.current_week_eur.toFixed(2)}</p>
                            </div>
                            {!isStaff && (
                              <button
                                onClick={() => grantAiCredit(u.user_id, u.name)}
                                className="px-2 py-1 text-xs rounded-md bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 font-semibold whitespace-nowrap"
                                title={`Dar más crédito AI a ${u.name} esta semana`}
                                data-testid={`grant-credit-${u.user_id}`}
                              >
                                +€ Geben
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <p className="text-[11px] text-[#A8A29E] italic">
                Hinweis: Der Wert ist eine grobe Schätzung basierend auf fixen Call-Kosten (Chat ≈ 0,5 ct, STT ≈ 2 ct, TTS ≈ 0,5 ct). Die tatsächlichen Anbieter-Rechnungen können leicht abweichen.
              </p>
            </>
          ) : (
            <p className="text-center text-sm text-red-600 py-8">Keine Daten geladen.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default TeacherDashboard;


// ── Materials trash section ─────────────────────────────────────────────────
// Lightweight, self-contained component so the giant TeacherDashboard state
// blob doesn't need yet another reducer. Mounts only when the tab is active.
const MaterialsTrashSection = () => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/teacher/materials/trash`, { withCredentials: true });
      setItems(res.data || []);
    } catch {
      toast.error('Fehler beim Laden des Material-Papierkorbs');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const restore = async (m) => {
    try {
      await axios.post(`${API}/materials/${m.material_id}/restore`, {}, { withCredentials: true });
      toast.success(`Wiederhergestellt: ${m.title || 'Material'}`);
      setItems(prev => prev.filter(x => x.material_id !== m.material_id));
    } catch {
      toast.error('Fehler beim Wiederherstellen');
    }
  };

  const purge = async (m) => {
    if (!window.confirm(`„${m.title || 'Material'}" endgültig löschen? Dies kann NICHT rückgängig gemacht werden.`)) return;
    try {
      await axios.delete(`${API}/materials/${m.material_id}/purge`, { withCredentials: true });
      toast.success('Endgültig gelöscht');
      setItems(prev => prev.filter(x => x.material_id !== m.material_id));
    } catch {
      toast.error('Fehler beim endgültigen Löschen');
    }
  };

  const emptyAll = async () => {
    if (items.length === 0) return;
    if (!window.confirm(`Den gesamten Material-Papierkorb (${items.length} Einträge) endgültig leeren? Dies kann NICHT rückgängig gemacht werden.`)) return;
    try {
      const res = await axios.post(`${API}/materials/trash/empty`, {}, { withCredentials: true });
      toast.success(`${res.data?.count ?? 0} Materialien endgültig gelöscht`);
      setItems([]);
    } catch {
      toast.error('Fehler beim Leeren');
    }
  };

  const filtered = items.filter(m => !search ||
    (m.title || '').toLowerCase().includes(search.toLowerCase()) ||
    (m.deleted_by_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (m.category || '').toLowerCase().includes(search.toLowerCase())
  );

  const categoryLabel = (c) => ({
    speaking: 'Sprechen', listening: 'Hören', reading_exercises: 'Lesen',
    writing: 'Schreiben', vocabulary: 'Vokabeln', exercises: 'Übungen',
    grammar: 'Grammatik', conversation: 'Conversación',
  })[c] || c;

  return (
    <div className="space-y-4" data-testid="materials-trash-tab">
      <div className="bg-gradient-to-br from-amber-50 to-white rounded-2xl border border-amber-200 p-5 flex items-start gap-3">
        <Warning size={22} className="text-amber-600 flex-shrink-0 mt-0.5" weight="fill" />
        <div className="flex-1">
          <h3 className="font-heading font-semibold text-[#292524]">Material-Papierkorb</h3>
          <p className="text-sm text-[#78716C] mt-1">
            Gelöschte Unterrichtsmaterialien (Sprechen, Hören, Schreiben, Lesen, Vokabeln, Übungen) bleiben hier dauerhaft,
            bis du sie endgültig löschst. Kommentare und Lehrerkorrekturen werden bei der Wiederherstellung beibehalten.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#D6D3D1] p-4">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h3 className="font-heading font-semibold text-lg">Gelöschte Materialien ({items.length})</h3>
          <div className="flex items-center gap-2 flex-1 justify-end min-w-[200px]">
            <Input placeholder="Suchen (Titel, Kategorie, Lehrer)..." className="w-full sm:w-[280px]"
              value={search} onChange={e => setSearch(e.target.value)} data-testid="materials-trash-search" />
            <Button onClick={load} variant="outline" size="sm" className="flex-shrink-0">
              <ArrowCounterClockwise size={14} className="mr-1" /> Aktualisieren
            </Button>
            {items.length > 0 && (
              <Button onClick={emptyAll} variant="outline" size="sm"
                className="flex-shrink-0 text-red-600 border-red-200 hover:bg-red-50"
                data-testid="materials-trash-empty-btn">
                <Trash size={14} className="mr-1" weight="fill" /> Alles leeren
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size={28} className="animate-spin text-[#AB1C24]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10">
            <Trash size={36} className="mx-auto text-[#D6D3D1] mb-3" />
            <p className="text-[#78716C]">
              {items.length === 0 ? 'Keine gelöschten Materialien.' : 'Keine Treffer für deine Suche.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {filtered.map(m => {
              const deletedDate = m.deleted_at ? new Date(m.deleted_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
              return (
                <div key={m.material_id} className="flex items-start justify-between gap-3 bg-[#FAF9F6] rounded-xl px-4 py-3 hover:bg-stone-100"
                  data-testid={`materials-trash-row-${m.material_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs bg-[#AB1C24]/10 text-[#AB1C24] px-2 py-0.5 rounded-full font-medium">
                        {categoryLabel(m.category)}
                      </span>
                      {m.level && <span className="text-xs px-2 py-0.5 rounded-full bg-white border border-stone-200">{m.level}</span>}
                      {(m.levels || []).length > 1 && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-white border border-stone-200">
                          {m.levels.join(', ')}
                        </span>
                      )}
                    </div>
                    <p className="font-medium text-[#292524] truncate line-through decoration-stone-400">{m.title || '(ohne Titel)'}</p>
                    {m.description && <p className="text-xs text-[#78716C] truncate mt-0.5">{m.description}</p>}
                    <p className="text-[10px] text-[#A8A29E] mt-1">
                      Gelöscht am {deletedDate}
                      {m.deleted_by_name && <span> · von <strong>{m.deleted_by_name}</strong></span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button onClick={() => restore(m)} size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white h-8 px-3"
                      data-testid={`restore-material-${m.material_id}`}>
                      <ArrowCounterClockwise size={14} className="mr-1" weight="bold" />
                      Wiederherstellen
                    </Button>
                    <button onClick={() => purge(m)}
                      className="p-2 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600"
                      title="Endgültig löschen"
                      data-testid={`purge-material-${m.material_id}`}>
                      <Trash size={16} weight="fill" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
